
import { BoolHelper, FileInputPreviewType, NoseurObject } from "@rivtn/noseur";
import { ApiResponseData, BaseService, IAnalyticsData, IEarningSource, IGallery, INotification, IWebsitePage, SanitizedResponse, TransactionSourceType, TransactionStatus, TransactionType, Visibility } from "../BaseService";
import { IProgram, ProgramService, TeamTypes, TeamTypesValue } from "./ProgramService";
import { ITeam, ITeamMember } from "./TeamService";
import { IHost, HostTestService } from "./HostService";
import { NotificationGroup, Notifier } from "../../components/shared/Notifiier";
import { IMember } from "./MemberService";
import { i18nManager } from "../../i18n";
import { Utils } from "../../utils";

export enum TournamentStatus {

    ENDED = "ENDED",
    DRAFT = "DRAFT",
    ONGOING = "ONGOING",
    UPCOMING = "UPCOMING",
    REGISTERED = "REGISTERED", // wannabe

}

export enum TournamentMatchStatus {

    ENDED = "ENDED",
    ONGOING = "ONGOING",
    UPCOMING = "UPCOMING",
    FORFEITED = "FORFEITED",
    CANCELLED = "CANCELLED",

}

export enum RegisteredTournamentStatus {

    REMOVED = "REMOVED",
    WITHDRAWN = "WITHDRAWN",
    REGISTERED = "REGISTERED",

}

export enum RegisteredTournamentParticipantType {

    TEAM = "TEAM",
    MEMBER = "MEMBER",

}

export enum TournamentCustomFieldType {

    TEXT = "TEXT",
    DATE = "DATE",
    CHECK = "CHECK",
    NUMBER = "NUMBER",
    OPTIONS = "OPTIONS",

}

export enum TournamentPrizeType {

    MONETARY = "MONETARY",
    BADGE_ONLY = "BADGE_ONLY",
    PHYSICAL_PRIZE = "PHYSICAL_PRIZE",
    MONETARY_AND_PHYSICAL_PRIZE = "MONETARY_AND_PHYSICAL_PRIZE",

}

export enum TournamentScoringSystem {

    RANK = "RANK",
    SCORE = "SCORE",
    POINTS = "POINTS",
    DURATION = "DURATION",

}

export enum TournamentMatchResultCategory {

    OWN = "OWN",
    FINAL = "FINAL",
    CURRENT = "CURRENT",

}

export enum TournamentMatchEndScore {

    WON = "WON",
    DRAW = "DRAW",
    LOST = "LOST",
    ABSENT = "ABSENT",
    FORFEITED = "FORFEITED",

}

export enum TournamentEditionScoringAppliesTo {

    INDIVIDUAL = "INDIVIDUAL",
    PARTICIPANT = "PARTICIPANT",

}

export enum TournamentEditionScoringType {

    CUSTOM_SCORE = "CUSTOM_SCORE",
    TOURNAMENT_SCORE = "TOURNAMENT_SCORE",

}

export enum TournamentEditionScoringTieBreaker {

    POLL = "POLL",
    NONE = "NONE",
    BUCHHOLZ = "BUCHHOLZ",
    HEAD_TO_HEAD = "HEAD_TO_HEAD",
    SONNEBORN_BERGER_SCORE = "SONNEBORN_BERGER_SCORE",
    SECONDARY_SCORE_DIFFERENCE = "SECONDARY_SCORE_DIFFERENCE",

}

export interface ITournamentEditionPrize {
    title: string;
    logo_url: string;
    position?: number;
    external_id: string;
    type: "TOP_WINNER" | "TROPHY";
    prize_type: TournamentPrizeType;
    currency_prizes?: NoseurObject<number>;
    projected_winner?: ITournamentEditionRegisteredParticipant;
    manually_selected_winner?: ITournamentEditionRegisteredParticipant;
    physical_prize?: {
        name: string;
        image_url: string;
        how_to_claim: string;
        detail_link?: string;
        prize_claimed?: boolean;
        available_in_all_countries: boolean;
    };
}

export interface ITournament {
    host: IHost;
    name: string;
    public_id: string;
    external_id: string;
    description?: string;
    extra_data: NoseurObject<any>;
    current_edition: ITournamentEdition;
}

export interface ITournamentEdition {
    name: string;
    password?: string;
    program: IProgram;
    logo_url?: string;
    program_id: string;
    created_at: string;
    updated_at: string;
    banner_url?: string;
    external_id: string;
    description?: string;
    country_code: string;
    last_updated: string;
    tournament: ITournament;
    status: TournamentStatus;
    extra_data: NoseurObject<any>;
    participant_members_count?: number;
    physical_address_location?: string;
    location: "PHYSICAL" | "ONLINE" | string;
    stage: string;
    format: string;
    is_template: boolean;
    registration_fees: NoseurObject<number>;
    un_reads: {
        messages: number;
        notifications: number;
    };
    details_settings: {
        is_private: boolean;
        invite_only: boolean;
        is_recurring: boolean;
    };
    participants_settings: {
        platforms?: string[];
        score_system?: string;
        professional: boolean;
        top_xin_program?: number;
        non_professional: boolean;
        premium_members_only: boolean;
        allow_incomplete_team: boolean;
        allowed_extra_team_members?: number;
        participant_per_registration?: number;
        previous_tournament_participant: boolean;
        previous_participant_in_tournaments_from_same_host: boolean;
    };
    dates: {
        tournament_end_date?: string;
        tournament_start_date?: string;
        registration_end_date?: string;
        registration_start_date?: string;
        result_announcement_date?: string;
        prize_pool_distribution_end_date?: string;
        prize_pool_distribution_start_date?: string;
    };
    registration: NoseurObject<{
        fee_amount: number;
        max_participants: number;
    }>;
    registration_settings: {
        bear_platform_fee_cost?: boolean;
        show_participants_count_on_listing?: boolean;
        prevent_participants_engaged_in_other_tournaments?: boolean;
    };
    meta: {
        public_id: string;
        primary_score?: string;
        total_matches?: number;
        result_announced?: boolean;
        price_pool_physical?: boolean;
        price_pool_monetary?: boolean;
        prize_pool_distributed?: boolean;
        total_expected_participants: number;
        total_registered_participants: number;
        initial_score_for_tie_breaking?: string;
        price_pool_amounts?: NoseurObject<number>;
        price_pool_physical_prize_available_in_all_countries?: boolean;
    };

    tie_breaker: string;
    scoring: NoseurObject<ITournamentEditionScoreSetting>;
    ___custom_fields___?: ITournamentEditionCustomField[];


    registered: boolean;
    team_type: TeamTypes;

    rule?: string;
    currency_code: string;
    host_public_id: string;
    previous_earning: number;
    forecast_earning: number;
    unique_participant: number;
    withdrawal?: {
        hours_before_registration_end_dates: number;
        refund_hours_before_registration_end_dates: number;
    };
};

export interface ITournamentEditionRegistered {
    pass_id: string;
    seed_number: number;
    external_id: string;
    registration_date?: string;
    tournament_edition_id?: string;
    use_banner_background?: boolean; // MOVE
    status: RegisteredTournamentStatus;
    withdrawal_or_removal_date?: string;
    withdrawal_or_removal_reason?: string;
    tournament_edition: ITournamentEdition;
    custom_fields_answers: NoseurObject<any>;
    custom_fields_id_map: NoseurObject<ITournamentEditionCustomField>;
    registration_amount: {
        amount: number;
        charge_fee: number;
        currency_code: string;
    };
    refund_amount?: {
        amount: number;
        charge_fee: number;
        currency_code: string;
    };
    pass_settings: {
        design_id: string;
        scheme_settings?: NoseurObject<string>;
    };
    own_standings: {
        position: number;
        count_above: number;
        count_below: number;
        total_positions: number;
    };
    own_matches_detail: {
        matches_won: number;
        matches_lost: number;
        matches_left: number;
        matches_total: number;
        matches_drawn: number;
        tournament_total_matches: number;
    };
    un_reads?: {
        notifications?: number;
    };
    soft_cache?: {
        calender_pass_id?: string;
        matches_left_pass_id?: string;
    };
}

export interface ITournamentEditionRegisteredParticipant {
    external_id: string;
    resource_id: string;
    resource?: IMember | ITeam;
    scores?: NoseurObject<number>;
    participants: Partial<ITeamMember>[];
    type: RegisteredTournamentParticipantType;
    extra_participants: Partial<ITeamMember>[];
    tournament_edition_registered: ITournamentEditionRegistered;
    un_reads: {
        messages: number;
        notifications: number;
    };
}

export interface ITournamentEditionCustomField {
    label: string;
    required: boolean;
    options?: string[];
    external_id: string;
    type: TournamentCustomFieldType;
}

export interface ITournamentEditionMatchResultProof {
    name: string;
    size: number;
    location_url: string;
    participant_name?: string;
    type: FileInputPreviewType;

    __file__?: File;
}

export interface ITournamentEditionMatchResult {
    date: string;
    submitter_id: string;
    scores: NoseurObject<NoseurObject<string>>;
    proofs: ITournamentEditionMatchResultProof[];
    tournament_scoring: ITournamentEditionScoring;
    tournament_edition_registered_participants?: ITournamentEditionRegisteredParticipant[];
}

//<String, Map<String, Map<String, Integer>>>
export interface ITournamentEditionMatch {
    end_date: string;
    location?: string;
    start_date: string;
    external_id: string;
    group_number?: number;
    match_number?: number;
    status: TournamentMatchStatus;
    extra_data: NoseurObject<any>;
    score?: TournamentMatchEndScore;
    cancel_or_forfeit_reason?: string;
    tournament_edition: ITournamentEdition;
    scores: NoseurObject<NoseurObject<NoseurObject<number>>>;
    tournament_edition_registered_participant_ids: string[];
    tournament_edition_registered_participant_proofs: NoseurObject<string[]>;
    final_tournament_proofs: NoseurObject<ITournamentEditionMatchResultProof[]>;
    tournament_edition_registered_participant_scores: NoseurObject<NoseurObject<any[]>>;
    tournament_edition_registered_participants: ITournamentEditionRegisteredParticipant[];
    tournament_edition_registered_participant_losers: NoseurObject<NoseurObject<NoseurObject<number>>>;
    tournament_edition_registered_participant_winners: NoseurObject<NoseurObject<NoseurObject<number>>>;
    tournament_edition_registered_participant_drawers: NoseurObject<NoseurObject<NoseurObject<number>>>;
    tournament_edition_registered_participant_submitted_results: NoseurObject<NoseurObject<ITournamentEditionMatchResult[]>>;

    __payload__: any;
    determining_score?: string; // put inside extr adata
}

export interface ITournamentCurrencyBalance {
    external_id: string;
    currency_code: string;
    tournament_id: string;
    balance_amount: number;
    transferred_amount: number;
    percentage_increase: number;
    projected_return_amount: number;
    manually_allocated_amount: number;
}

export interface ITournamentTransaction {
    tax: number;
    amount: number;
    created_at: string;
    description: string;
    external_id: string;
    currency_code: string;
    type: TransactionType;
    payable_amount: number;
    platform_amount: number;
    source: TransactionType;
    status: TransactionStatus;
    extra_data: NoseurObject<any>;
}

export interface ITournamentEditionAnalytics {
    matches?: {
        total?: IAnalyticsData;
        ended?: IAnalyticsData;
        ongoing?: IAnalyticsData;
        upcoming?: IAnalyticsData;
    };
    earnings?: {
        total?: IAnalyticsData;
        merchandise?: IAnalyticsData;
        registration?: IAnalyticsData;
        advertisement?: IAnalyticsData;
    };
    participants?: {
        new?: IAnalyticsData;
        total?: IAnalyticsData;
        removed?: IAnalyticsData;
        withdrawn?: IAnalyticsData;
    };
    prize_pool?: {
        total?: IAnalyticsData;
        trophy?: IAnalyticsData;
        monetary?: IAnalyticsData;
        physical?: IAnalyticsData;
    };
}

export interface ITournamentEditionPrizePoolActivities {
    log: string;
    created_at: string;
    external_id: string;
}

export interface ITournamentEditionScoreSetting {
    id: string;
    name: string;
    short_name: string;
    score_per_win: number;
    score_per_loss: number;
    score_per_draw: number;
    starting_score: number;
    submission_name: string;
    higher_is_winner: boolean;
    show_on_match_pages: boolean;
    score_system: TournamentScoringSystem;
    allow_participant_submit_score: boolean;
}

export interface ITournamentEditionScoring {
    name: string;
    external_id: string;
    extra_data: NoseurObject<any>;
    status: "ACTIVE" | "IN_ACTIVE";
    show_on_tournament_pages?: boolean;
    type: TournamentEditionScoringType;
    tournament_prize_pool_ids: string[];
    allow_participant_submit_score: boolean;
    applies_to: TournamentEditionScoringAppliesTo;
    tie_breaker: TournamentEditionScoringTieBreaker;
    leader?: ITournamentEditionRegisteredParticipant;
    tournament_prize_pools: ITournamentEditionPrize[];
    scoring: NoseurObject<ITournamentEditionScoreSetting>;
}

export interface ITournamentEditionScoringRecord {
    position: number;
    external_id: string;
    __new_position__: number;
    scores: NoseurObject<number>;
    tournament_score: ITournamentEditionScoring;
    tournament_edition_registered_participant: ITournamentEditionRegisteredParticipant;
}

export interface ITournamentEditionWebsite {
    rules: string;
    details: string;
    sponsors: string[];
    visibility: Visibility;
    extra_data: NoseurObject<any>;
    social_accounts: NoseurObject<string>;
    media: {
        LOGO: string[];
        BANNER: string[];
    };
    website_pages: {
        HOME: IWebsitePage;
        ABOUT: IWebsitePage;
        MERCH: IWebsitePage;
        EVENTS: IWebsitePage;
        GALLERY: IWebsitePage;
        TROPHIES: IWebsitePage;
        SCHEDULE: IWebsitePage;
        LEADERBOARD: IWebsitePage;
        PARTICIPANTS: IWebsitePage;
    };
    mobile_app_pages: {
        MERCH: IWebsitePage;
        ABOUT: IWebsitePage;
        EVENTS: IWebsitePage;
        SEARCH: IWebsitePage;
        SCHEDULE: IWebsitePage;
        TROPHIES: IWebsitePage;
        SETTINGS: IWebsitePage;
        MESSAGES: IWebsitePage;
        EARNINGS: IWebsitePage;
        DASHBOARD: IWebsitePage;
        LEADERBOARD: IWebsitePage;
        PARTICIPANTS: IWebsitePage;
        NOTIFICATIONS: IWebsitePage;
    };
    home_settings: {
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        show_top_review: boolean;
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
        show_top_participants: boolean;
    };
    events_settings: {
        enable_rivtn_ad: boolean;
    };
    merchandise_settings: {
        storefront_id: string;
        enable_rivtn_ad: boolean;
    };
    participants_settings: {
        enable_rivtn_ad: boolean;
        show_current_participants: boolean;
        show_previous_participants: boolean;
    };
    schedule_settings: {
        enable_rivtn_ad: boolean;
        show_past_matches: boolean;
        show_ongoing_upcoming_matches: boolean;
    };
    trophies_settings: {
        enable_rivtn_ad: boolean;
        enable_cross_filter: boolean;
        show_monetary_prize: boolean;
        show_physical_prize: boolean;
    };
    leaderboard_settings: {
        enable_rivtn_ad: boolean;
        show_participants_form: boolean;
        show_participants_wld_record: boolean;
        custom_score_settings: NoseurObject<any>;
        show_all_custom_score_leaderboard: boolean;
    },
    gallery_settings: {
        enable_rivtn_ad: boolean;
    };
    about_settings: {
        show_faqs: boolean;
        show_rules: boolean;
        show_reviews: boolean;
        show_details: boolean;
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        enable_rivtn_ad_on_faqs: boolean;
    };
    website_design_settings: {
        design_id: string;
        scheme_settings: NoseurObject<any>;
    };
    mobile_design_settings: {
        design_id: string;
        scheme_settings: NoseurObject<any>;
    };
}

export interface ITournamentEditionEvent {
    title: string;
    details: string;
    end_date?: string;
    location?: string;
    start_date?: string;
    external_id: string;
    action_label?: string;
    media_location: string;
    action_location?: string;
}

export interface ITournamentEditionFaqs {
    answer: string;
    question: string;
    external_id: string;
}

export class TournamentTestService {

    static TEST_TOURNAMENTS: Partial<any>[] = [
        {
            public_id: "1",
            external_id: "1",
            registered: false,
            status: "ONGOING",
            country_code: "NGA",
            currency_code: "NGN",
            name: "Cruise Royale",
            unique_participant: 200,
            team_type: TeamTypes.SOLO,
            previous_earning: 2700384,
            forecast_earning: 1700384,
            location: "21, First Street. Ibadan",
            host: HostTestService.TEST_HOSTS[0],
            last_updated: "2025-09-03T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[4] as IProgram,
            rule: `
Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi eu maximus dolor. Aenean vitae bibendum urna, vitae hendrerit velit. Aenean ornare tincidunt gravida. Nulla et lectus lacus. Interdum et malesuada fames ac ante ipsum primis in faucibus. Praesent ac nibh et ante fringilla commodo. Etiam sit amet aliquet massa. Sed gravida enim sem, et consectetur sapien lobortis eget. Morbi nibh lacus, tincidunt et mollis id, ullamcorper a urna.

Donec risus lorem, pulvinar ac dictum quis, blandit at nibh. Aliquam varius sagittis nisl at rhoncus. Sed dolor nibh, egestas ut metus id, rhoncus consectetur lorem. Morbi non turpis eu nisi gravida tempor. Praesent ut pulvinar lacus. Vestibulum suscipit orci massa, vel aliquam nisi hendrerit ut. Curabitur tellus neque, facilisis id porta porta, condimentum congue nulla. Mauris gravida aliquam velit et dapibus. Nullam rhoncus eu neque nec ultricies. Nulla aliquet, augue a blandit pharetra, libero nibh interdum urna, vitae cursus erat arcu non mi. Proin ut fermentum ex. Curabitur semper iaculis ornare. Aenean vitae euismod augue. Aliquam suscipit elementum mollis. Ut ut sodales sapien, non posuere lectus.

Pellentesque condimentum tincidunt eros, eget finibus ligula. Etiam luctus leo ac mi ultricies, a pulvinar erat commodo. Ut eleifend non purus ac scelerisque. Morbi semper tellus at ipsum lacinia, a sollicitudin mi consequat. Proin nec ligula erat. Maecenas scelerisque vel orci id ullamcorper. Nullam ultrices, diam eget finibus consequat, justo massa tincidunt purus, in rutrum magna elit a magna. Vestibulum feugiat pharetra ante, elementum blandit diam interdum eu. Cras elit massa, fermentum dignissim feugia

Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi eu maximus dolor. Aenean vitae bibendum urna, vitae hendrerit velit. Aenean ornare tincidunt gravida. Nulla et lectus lacus. Interdum et malesuada fames ac ante ipsum primis in faucibus. Praesent ac nibh et ante fringilla commodo. Etiam sit amet aliquet massa. Sed gravida enim sem, et consectetur sapien lobortis eget. Morbi nibh lacus, tincidunt et mollis id, ullamcorper a urna.

Donec risus lorem, pulvinar ac dictum quis, blandit at nibh. Aliquam varius sagittis nisl at rhoncus. Sed dolor nibh, egestas ut metus id, rhoncus consectetur lorem. Morbi non turpis eu nisi gravida tempor. Praesent ut pulvinar lacus. Vestibulum suscipit orci massa, vel aliquam nisi hendrerit ut. Curabitur tellus neque, facilisis id porta porta, condimentum congue nulla. Mauris gravida aliquam velit et dapibus. Nullam rhoncus eu neque nec ultricies. Nulla aliquet, augue a blandit pharetra, libero nibh interdum urna, vitae cursus erat arcu non mi. Proin ut fermentum ex. Curabitur semper iaculis ornare. Aenean vitae euismod augue. Aliquam suscipit elementum mollis. Ut ut sodales sapien, non posuere lectus.

Pellentesque condimentum tincidunt eros, eget finibus ligula. Etiam luctus leo ac mi ultricies, a pulvinar erat commodo. Ut eleifend non purus ac scelerisque. Morbi semper tellus at ipsum lacinia, a sollicitudin mi consequat. Proin nec ligula erat. Maecenas scelerisque vel orci id ullamcorper. Nullam ultrices, diam eget finibus consequat, justo massa tincidunt purus, in rutrum magna elit a magna. Vestibulum feugiat pharetra ante, elementum blandit diam interdum eu. Cras elit massa, fermentum dignissim feugia
            `,
            logo_urls: [
                "https://fbi.cults3d.com/uploaders/15626798/illustration-file/2c61fec7-87ec-439a-9cfc-fddb8c688f18/Warzone.png",
            ],
            banner_urls: [
                "https://blz-contentstack-images.akamaized.net/v3/assets/bltf408a0557f4e4998/blt476e411d696dfb08/67e603a1d66ebd1287f688e5/BO6_S3_WZ_X1-Garrison-App-1920x1080.png?imwidth=1920&imdensity=2.625"
            ],
            participants: {
                joined: 21,
                extra_team_members: 2,
                total_acceptable: 100,
                allow_incomplete_member: false,
            },
            registration: {
                NGN: {
                    fee_amount: 500000,
                    max_participants: 100
                }
            },
            dates: {
                tournament_end_date: "2025-09-03T12:08:39.000+00:00",
                tournament_start_date: "2025-12-03T12:08:39.000+00:00",
                registration_end_date: "2025-07-23T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: true,
                physical: false,
                physical_prize_available_in_all_countries: true,
                amounts: {
                    USD: 1200,
                    KES: 500000,
                    GHS: 50000000,
                },
                trophies: [

                ],
                top_winners: [

                ],
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 3,
                top_rated_in_program: true,
                non_professional_only: true,
                previous_tournament_participant: true,
                dont_allow_incomplete_team_members: false,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 5,
                notifications: 2,
            },
        },
        {
            public_id: "2",
            external_id: "2",
            status: "ENDED",
            registered: false,
            country_code: "GHA",
            currency_code: "NGN",
            name: "Forth Nights",
            unique_participant: 40,
            previous_earning: 3000200,
            forecast_earning: 3000200,
            team_type: TeamTypes.SQUAD,
            location: "87, Street. Lagos",
            host: HostTestService.TEST_HOSTS[0],
            last_updated: "2025-10-03T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[1] as IProgram,
            logo_urls: [
                "https://cdn1.epicgames.com/offer/fn/FNECO_36-10_ForbiddenFruit_EGS_Launcher_KeyArt_Blade_1200x1600_1200x1600-9eb41f23a725f28a00996272bf654e8e"
            ],
            banner_urls: [
                "https://assets.nintendo.com/image/upload/ar_16:9,c_lpad,w_1240/b_white/f_auto/q_auto/ncom/software/switch/70010000010192/f26fc9e1b11ce01369966ed9225e320a293c4eaad1329774f125e05629ffd437"
            ],
            participants: {
                joined: 100,
                extra_team_members: 1,
                total_acceptable: 100,
                allow_incomplete_member: false,
            },
            registration: {
                NGN: {
                    fee_amount: 500000,
                    max_participants: 100
                }
            },
            dates: {
                tournament_end_date: "2025-09-03T12:08:39.000+00:00",
                tournament_start_date: "2025-12-03T12:08:39.000+00:00",
                registration_end_date: "2025-07-23T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                physical: false,
                monetary: false,
                physical_prize_available_in_all_countries: true,
            },
            participant_requirements: {
                professional_only: false,
                premium_users_only: true,
                top_x_rated_in_program: 3,
                top_rated_in_program: false,
                non_professional_only: true,
                previous_tournament_participant: false,
                dont_allow_incomplete_team_members: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 0,
                notifications: 2,
            },
        },
        {
            public_id: "3",
            external_id: "3",
            registered: true,
            status: "DRAFT",
            country_code: "GHA",
            currency_code: "NGN",
            name: "Free Players",
            unique_participant: 90,
            previous_earning: 9000384,
            forecast_earning: 6000384,
            team_type: TeamTypes.SEXTET,
            host: HostTestService.TEST_HOSTS[1],
            last_updated: "2025-11-03T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[12] as IProgram,
            logo_urls: [
                "https://cdn2.steamgriddb.com/icon_thumb/c7a45163eda01f63ab1ddb70d0f086fe.png"
            ],
            banner_urls: [
                "https://assets.goal.com/images/v3/blt178d34156522b281/GOAL%20-%20Blank%20WEB%20-%20Facebook%20-%202024-07-17T151013.046.jpg?auto=webp&format=pjpg&width=3840&quality=60"
            ],
            participants: {
                joined: 21,
                extra_team_members: 3,
                total_acceptable: 100,
                allow_incomplete_member: false,
            },
            registration: {
                NGN: {
                    fee_amount: 90000,
                    max_participants: 100
                }
            },
            dates: {
                tournament_end_date: "2025-11-03T12:08:39.000+00:00",
                tournament_start_date: "2025-06-03T12:08:39.000+00:00",
                registration_end_date: "2025-06-23T12:08:39.000+00:00",
                registration_start_date: "2025-06-03T12:08:39.000+00:00",
                result_announcement_date: "2025-11-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-11-10T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-11-04T12:08:39.000+00:00"
            },
            price_pool: {
                physical: true,
                monetary: true,
                physical_prize_available_in_all_countries: true,
                amounts: {
                    NGN: 920000000,
                }
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: false,
                top_x_rated_in_program: 10,
                top_rated_in_program: false,
                non_professional_only: false,
                dont_allow_incomplete_team_members: false,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 3,
                notifications: 2,
            },
        },
        {
            public_id: "4",
            external_id: "4",
            registered: false,
            status: "PENDING",
            country_code: "KEN",
            currency_code: "NGN",
            unique_participant: 91,
            name: "Chicken Dinnerer",
            team_type: TeamTypes.DUO,
            previous_earning: 9800384,
            forecast_earning: 10384,
            host: HostTestService.TEST_HOSTS[2],
            last_updated: "2025-12-03T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[13] as IProgram,
            logo_urls: [
                "https://play-lh.googleusercontent.com/vkziTNqdZU0su1t-AdjiCyre0SSnHTQJmznppAfbGTtj98yzttsChqcvUOihEkXDsoNZ"
            ],
            banner_urls: [
                "https://www.itp.net/cloud/2021/08/19/SADG6MgH-PUBGMobile-1-1-1200x675.jpg"
            ],
            participants: {
                joined: 27,
                total_acceptable: 50,
                extra_team_members: 0,
                allow_incomplete_member: true,
            },
            registration: {
            },
            dates: {
                tournament_end_date: "2025-11-23T12:08:39.000+00:00",
                tournament_start_date: "2025-11-03T12:08:39.000+00:00",
                registration_end_date: "2025-06-23T12:08:39.000+00:00",
                registration_start_date: "2025-06-03T12:08:39.000+00:00",
                result_announcement_date: "2025-11-30T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-11-30T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-12-01T12:08:39.000+00:00"
            },
            price_pool: {
                physical: true,
                monetary: false,
                physical_prize_available_in_all_countries: false,
            },
            participant_requirements: {
                professional_only: false,
                premium_users_only: false,
                top_x_rated_in_program: 3,
                top_rated_in_program: false,
                non_professional_only: false,
                dont_allow_incomplete_team_members: false,
                previous_tournament_participant: false,
                previous_host_tournaments_participant: false,
            },
            un_reads: {
                messages: 4,
                notifications: 2,
            },
        },
        {
            public_id: "5",
            external_id: "5",
            registered: true,
            name: "RaceHeads",
            status: "IN_REGISTRATION",
            country_code: "NGA",
            currency_code: "NGN",
            unique_participant: 120,
            team_type: TeamTypes.TRIO,
            previous_earning: 43700323,
            forecast_earning: 23700323,
            location: "2 Road Ares, Ikeja, Lagos",
            host: HostTestService.TEST_HOSTS[0],
            last_updated: "2024-01-03T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[14] as IProgram,
            logo_urls: [
                "https://play-lh.googleusercontent.com/rDAmDCGhyX7FcXYJcR0QrcO04uWgdiQaiWw6oA3T93oK4Xq8qOof0-J_qtw2XFYpKg"
            ],
            banner_urls: [
                "https://www.teamvvv.com/wp-content/uploads/2016/02/Need-for-Speed-2015-header-art.jpg"
            ],
            participants: {
                joined: 0,
                total_acceptable: 10,
                extra_team_members: 1,
                allow_incomplete_member: true,
            },
            dates: {
                tournament_end_date: "2025-04-20T12:08:39.000+00:00",
                tournament_start_date: "2025-04-03T12:08:39.000+00:00",
                registration_end_date: "2025-03-23T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-05-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-03-01T12:08:39.000+00:00"
            },
            price_pool: {
                physical: true,
                monetary: true,
                physical_prize_available_in_all_countries: true,
                amounts: {
                    NGN: 10000000,
                }
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 3,
                top_rated_in_program: false,
                non_professional_only: false,
                dont_allow_incomplete_team_members: true,
                previous_tournament_participant: false,
                previous_host_tournaments_participant: false,
            },
            un_reads: {
                messages: 5,
                notifications: 2,
            },
        },
        {
            public_id: "6",
            external_id: "6",
            status: "CLOSED",
            registered: false,
            country_code: "USA",
            currency_code: "NGN",
            unique_participant: 31,
            name: "Emperor's Custodes",
            team_type: TeamTypes.SOLO,
            previous_earning: 53700323,
            forecast_earning: 903700323,
            location: "9-10 Game hall 2 Mark. Abuja",
            host: HostTestService.TEST_HOSTS[2],
            last_updated: "2024-02-03T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[15] as IProgram,
            logo_urls: [
                "https://upload.wikimedia.org/wikipedia/en/4/42/Warhammer_40%2C000_Space_Marine_2_cover_art.jpg"
            ],
            banner_urls: [
                "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/2183900/ss_66a8bc13bf868d0e2a76e4d797e6408c271065d7.1920x1080.jpg?t=1751643527"
            ],
            participants: {
                joined: 214,
                total_acceptable: -1,
                extra_team_members: 5,
                allow_incomplete_member: true,
            },
            registration: {
                NGN: {
                    fee_amount: 250000,
                    max_participants: 100
                }
            },
            dates: {
                tournament_end_date: "2025-06-03T12:08:39.000+00:00",
                tournament_start_date: "2025-05-03T12:08:39.000+00:00",
                registration_end_date: "2025-05-23T12:08:39.000+00:00",
                registration_start_date: "2025-05-03T12:08:39.000+00:00",
                result_announcement_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: true,
                physical: false,
                physical_prize_available_in_all_countries: true,
                amounts: {
                    NGN: 95000000,
                }
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 1,
                top_rated_in_program: false,
                non_professional_only: false,
                dont_allow_incomplete_team_members: false,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: false,
            },
            un_reads: {
                messages: 6,
                notifications: 2,
            },
        },
        {
            public_id: "7",
            external_id: "7",
            registered: false,
            status: "CANCELLED",
            country_code: "USA",
            currency_code: "NGN",
            unique_participant: 91,
            name: "Legends Never Die",
            team_type: TeamTypes.SQUAD,
            previous_earning: 70000323,
            forecast_earning: 40000323,
            host: HostTestService.TEST_HOSTS[0],
            last_updated: "2024-03-03T12:08:39.000+00:00",
            location: "9-10 Game hall 2 Mark. Abuja 9-10 Game hall 2 Mark. Abuja 9-10 Game hall 2 Mark. Abuja",
            program: ProgramService.TEST_PROGRAMS[16] as IProgram,
            logo_urls: [
                "https://upload.wikimedia.org/wikipedia/en/d/db/Apex_legends_cover.jpg"
            ],
            banner_urls: [
                "https://cdn1.epicgames.com/spt-assets/5dcd88f4e2094a698ebffa43438edc33/apex-legends-1ugyx.jpg?resize=1&w=480&h=270&quality=medium"
            ],
            participants: {
                joined: 23,
                total_acceptable: 90,
                extra_team_members: 1,
                allow_incomplete_member: false,
            },
            registration: {
                NGN: {
                    fee_amount: 20000,
                    max_participants: 23
                }
            },
            dates: {
                tournament_end_date: "2025-12-05T12:08:39.000+00:00",
                tournament_start_date: "2025-12-03T12:08:39.000+00:00",
                registration_end_date: "2025-12-06T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: true,
                physical: true,
                physical_prize_available_in_all_countries: true,
                amounts: {
                    NGN: 1780000,
                }
            },
            participant_requirements: {
                professional_only: false,
                premium_users_only: false,
                top_x_rated_in_program: 3,
                top_rated_in_program: false,
                non_professional_only: true,
                dont_allow_incomplete_team_members: true,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 7,
                notifications: 2,
            },
        },
        {
            public_id: "8",
            external_id: "8",
            registered: false,
            country_code: "GHA",
            currency_code: "NGN",
            status: "UPCOMING",
            name: "Clash Of Kings",
            unique_participant: 19,
            team_type: TeamTypes.TRIO,
            previous_earning: 31200323,
            forecast_earning: 451200323,
            location: "9-10 Game hall 2 Mark",
            host: HostTestService.TEST_HOSTS[1],
            last_updated: "2024-04-09T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[17] as IProgram,
            logo_urls: [
                "https://sm.ign.com/ign_za/cover/c/clash-roya/clash-royale_88kr.jpg"
            ],
            banner_urls: [
                "https://i.ytimg.com/vi/uWsQ5IWVilM/maxresdefault.jpg"
            ],
            participants: {
                joined: 23,
                total_acceptable: 90,
                extra_team_members: 8,
                allow_incomplete_member: false,
            },
            registration: {
                KES: {
                    fee_amount: 20000,
                    max_participants: 23
                },
                USD: {
                    fee_amount: 10000,
                    max_participants: 23
                }
            },
            dates: {
                tournament_end_date: "2025-05-05T12:08:39.000+00:00",
                tournament_start_date: "2025-04-03T12:08:39.000+00:00",
                registration_end_date: "2025-03-06T12:08:39.000+00:00",
                registration_start_date: "2025-02-03T12:08:39.000+00:00",
                result_announcement_date: "2025-05-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-11-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: false,
                physical: false,
                physical_prize_available_in_all_countries: false,
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 10,
                top_rated_in_program: true,
                non_professional_only: true,
                dont_allow_incomplete_team_members: true,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 8,
                notifications: 2,
            },
        },
        {
            public_id: "9",
            external_id: "9",
            registered: false,
            country_code: "GHA",
            currency_code: "NGN",
            status: "CLOSED",
            name: "The ORIGINals",
            unique_participant: 190,
            team_type: TeamTypes.SOLO,
            previous_earning: 8700323,
            forecast_earning: 21700323,
            host: HostTestService.TEST_HOSTS[2],
            last_updated: "2024-05-01T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[18] as IProgram,
            logo_urls: [
                "https://m.media-amazon.com/images/I/A1Lc+0V8SfL._UF1000,1000_QL80_.jpg"
            ],
            banner_urls: [
                "https://gaming-cdn.com/images/products/1843/orig/assassin-s-creed-origins-pc-game-ubisoft-connect-europe-cover.jpg?v=1731486408"
            ],
            participants: {
                joined: 23,
                total_acceptable: 90,
                extra_team_members: 1,
                allow_incomplete_member: false,
            },
            registration: {},
            dates: {
                tournament_end_date: "2025-12-05T12:08:39.000+00:00",
                tournament_start_date: "2025-12-03T12:08:39.000+00:00",
                registration_end_date: "2025-12-06T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: false,
                physical: false,
                physical_prize_available_in_all_countries: false,
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 10,
                top_rated_in_program: true,
                non_professional_only: true,
                dont_allow_incomplete_team_members: true,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 9,
                notifications: 2,
            },
        },
        {
            public_id: "10",
            external_id: "10",
            registered: false,
            status: "UPCOMING",
            country_code: "NGA",
            currency_code: "NGN",
            name: "Arena Champions",
            unique_participant: 902,
            team_type: TeamTypes.SOLO,
            previous_earning: 283700323,
            forecast_earning: 383700323,
            host: HostTestService.TEST_HOSTS[1],
            last_updated: "2024-06-02T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[19] as IProgram,
            logo_urls: [
                "https://upload.wikimedia.org/wikipedia/en/1/13/Paladins_%28video_game%29.jpg"
            ],
            banner_urls: [
                "https://cdn1.epicgames.com/offer/badb0ee71b474ed591ec43212547cfc8/Paladins_EGS_BaseGame_2560x1440_2560x1440-99d0ed45c628c499ad600a52217b4123"
            ],
            participants: {
                joined: 12,
                total_acceptable: 15,
                extra_team_members: 2,
                allow_incomplete_member: false,
            },
            registration: {
                NGN: {
                    fee_amount: 250000,
                    max_participants: 15
                }
            },
            dates: {
                tournament_end_date: "2025-12-05T12:08:39.000+00:00",
                tournament_start_date: "2025-12-03T12:08:39.000+00:00",
                registration_end_date: "2025-12-06T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: false,
                physical: false,
                physical_prize_available_in_all_countries: false,
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 10,
                top_rated_in_program: true,
                non_professional_only: true,
                dont_allow_incomplete_team_members: true,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 10,
                notifications: 2,
            },
        },
        {
            public_id: "11",
            external_id: "11",
            registered: false,
            status: "DRAFt",
            country_code: "GHA",
            currency_code: "NGN",
            unique_participant: 91,
            name: "Unknown Warriors",
            team_type: TeamTypes.SQUAD,
            previous_earning: 3700323,
            forecast_earning: 100323,
            location: "10 Game hall 2 Mark",
            host: HostTestService.TEST_HOSTS[1],
            last_updated: "2024-07-13T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[20] as IProgram,
            logo_urls: [
                "https://tse4.mm.bing.net/th/id/OIP.wHxHd6Xp94K9Kmn143Nb4gHaHa?rs=1&pid=ImgDetMain&o=7&rm=3"
            ],
            banner_urls: [
                "https://wstatic-prod.pubg.com/web/live/static/og/img-og-pubg.jpg"
            ],
            participants: {
                joined: 23,
                total_acceptable: 90,
                extra_team_members: 8,
                allow_incomplete_member: true,
            },
            registration: {
                KES: {
                    fee_amount: 20000,
                    max_participants: 23
                },
                USD: {
                    fee_amount: 10000,
                    max_participants: 23
                }
            },
            dates: {
                tournament_end_date: "2025-12-05T12:08:39.000+00:00",
                tournament_start_date: "2025-12-03T12:08:39.000+00:00",
                registration_end_date: "2025-12-06T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: false,
                physical: false,
                physical_prize_available_in_all_countries: false,
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 10,
                top_rated_in_program: true,
                non_professional_only: true,
                dont_allow_incomplete_team_members: true,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 11,
                notifications: 2,
            },
        },
        {
            public_id: "12",
            external_id: "12",
            registered: false,
            country_code: "GHA",
            currency_code: "NGN",
            name: "Super Brothers",
            unique_participant: 9,
            status: "IN_REGISTRATION",
            forecast_earning: 1370032,
            previous_earning: 1370032,
            team_type: TeamTypes.SQUAD,
            location: "10 Game hall 2 Mark",
            host: HostTestService.TEST_HOSTS[1],
            last_updated: "2024-08-23T12:08:39.000+00:00",
            program: ProgramService.TEST_PROGRAMS[21] as IProgram,
            logo_urls: [
                "https://mediaproxy.tvtropes.org/width/1200/https://static.tvtropes.org/pmwiki/pub/images/super_mario_5.png"
            ],
            banner_urls: [
                "https://occ-0-8407-90.1.nflxso.net/dnm/api/v6/Z-WHgqd_TeJxSuha8aZ5WpyLcX8/AAAABWQhaGCZ1c0xcMErKUhFPwccr2jkUATrmAgr4hQNs6g9Kbju0uBial7BGleQp7kVtSnOV9DajGGajrvzJbwQ-akLq-prJShN-la1.jpg?r=07c"
            ],
            participants: {
                joined: 23,
                total_acceptable: 90,
                extra_team_members: 8,
                allow_incomplete_member: false,
            },
            registration: {
                KES: {
                    fee_amount: 20000,
                    max_participants: 23
                },
                USD: {
                    fee_amount: 10000,
                    max_participants: 23
                }
            },
            dates: {
                tournament_end_date: "2025-12-05T12:08:39.000+00:00",
                tournament_start_date: "2025-12-03T12:08:39.000+00:00",
                registration_end_date: "2025-12-06T12:08:39.000+00:00",
                registration_start_date: "2025-07-03T12:08:39.000+00:00",
                result_announcement_date: "2025-03-03T12:08:39.000+00:00",
                prize_pool_distribution_end_date: "2025-10-03T12:08:39.000+00:00",
                prize_pool_distribution_start_date: "2025-10-01T12:08:39.000+00:00"
            },
            price_pool: {
                monetary: false,
                physical: false,
                physical_prize_available_in_all_countries: false,
            },
            participant_requirements: {
                professional_only: true,
                premium_users_only: true,
                top_x_rated_in_program: 10,
                top_rated_in_program: true,
                non_professional_only: true,
                dont_allow_incomplete_team_members: true,
                previous_tournament_participant: true,
                previous_host_tournaments_participant: true,
            },
            un_reads: {
                messages: 11,
                notifications: 2,
            },
        },
    ];

}

export class TournamentService extends BaseService {

    protected static instance: TournamentService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!TournamentService.instance) TournamentService.instance = new TournamentService();
        return TournamentService.instance;
    }

    // tournament

    MEMBER_COUNT_TO_TEAM_TYPE_COUNT = Object.fromEntries(
        Object.entries(TeamTypesValue).map(([key, value]) => [value, key])
    );

    resolveTournamentFields(tournamentEdition: ITournamentEdition) {
        tournamentEdition.team_type = this.MEMBER_COUNT_TO_TEAM_TYPE_COUNT[tournamentEdition.participant_members_count ?? -1] as TeamTypes ?? TeamTypes.ALL;
    }

    queryTournaments(params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournament>> {
        return this.report(this.transport.get("/v1/tournament", { query: this.normalizeParams(params) }));
    }

    getSingleTournament(tournmamentId: string) {
        return this.report(this.transport.get(`/v1/tournament/${tournmamentId}`));
    }

    updateTournament(tournament: Partial<ITournament>): Promise<SanitizedResponse<ITournament>> {
        return this.report(this.transport.patch(`/v1/tournament/${tournament.external_id}`, tournament));
    }

    closeTournament(tournamentId: string) {
        return this.report(this.transport.delete(`/v1/tournament/${tournamentId}`));
    }

    reactivateTournament(tournamentId: string) {
        return this.report(this.transport.put(`/v1/tournament/${tournamentId}/reactivate`, {}));
    }

    validateCanCreateNewTournamentEdition(tournmamentId: string) {
        return this.report(this.transport.get(`/v1/tournament/${tournmamentId}/create/pre`));
    }

    queryTournamentEditions(params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEdition>> {
        return this.report(this.transport.get("/v1/tournament/edition", { query: this.normalizeParams(params) }), ({ content }: SanitizedResponse<ITournamentEdition>) => {
            content.forEach(this.resolveTournamentFields.bind(this));
        });
    }

    createTournamentEdition(tournament: Partial<ITournamentEdition>): Promise<SanitizedResponse<ITournamentEdition>> {
        return this.report(this.transport.post("/v1/tournament/edition", tournament));
    }

    updateTournamentEdition(tournament: Partial<ITournamentEdition>): Promise<SanitizedResponse<ITournamentEdition>> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${tournament.external_id}`, tournament));
    }

    updateTournamentEditionStatus(tournamentId: string, status: TournamentStatus): Promise<SanitizedResponse<ITournamentEdition>> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${tournamentId}/status`, { status }));
    }

    getSingleTournamentEdition(tournmamentEditionId: string) {
        console.log("WE HERE....", tournmamentEditionId);
        return this.report(this.transport.get(`/v1/tournament/edition/${tournmamentEditionId}`));
    }

    // custom fields

    getTournamentEditionCustomFields(tournamentEditionId: string) {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentEditionId}/custom/field`));
    }

    createTournamentEditionCustomField(tournamentId: string, customField: Partial<ITournamentEditionCustomField>): Promise<SanitizedResponse<ITournamentEditionCustomField>> {
        return this.report(this.transport.post(`/v1/tournament/edition/${tournamentId}/custom/field`, customField));
    }

    updateTournamentEditionCustomField(tournamentId: string, customField: Partial<ITournamentEditionCustomField>): Promise<SanitizedResponse<ITournamentEditionCustomField>> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${tournamentId}/custom/field/${customField.external_id}`, customField));
    }

    deleteTournamentEditionCustomField(tournmamentEditionId: string, id: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournmamentEditionId}/custom/field/${id}`));
    }

    // prize pool

    getTournamentEditionPrizePool(tournamentId: string) {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentId}/prize/pool`));
    }

    createTournamentEditionPrizePool(tournamentId: string, prizePool: Partial<ITournamentEditionPrize>): Promise<SanitizedResponse<ITournamentEditionPrize>> {
        return this.report(this.transport.post(`/v1/tournament/edition/${tournamentId}/prize/pool`, prizePool));
    }

    updateTournamentEditionPrizePool(tournamentId: string, prizePool: Partial<ITournamentEditionPrize>): Promise<SanitizedResponse<ITournamentEditionPrize>> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${tournamentId}/prize/pool/${prizePool.external_id}`, prizePool));
    }

    deleteTournamentEditionPrizePool(tournmamentEditionId: string, id: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournmamentEditionId}/prize/pool/${id}`));
    }

    manuallyAttachTournamentEditionPrizePoolWinner(tournamentId: string, prizePoolId: string, participantId: string): Promise<SanitizedResponse<any>> {
        return this.report(this.transport.put(`/v1/tournament/edition/${tournamentId}/prize/pool/${prizePoolId}/winner`, {
            tournament_edition_registered_participant_id: participantId
        }));
    }

    removeManuallyAttachTournamentEditionPrizePoolWinner(tournamentId: string, prizePoolId: string): Promise<SanitizedResponse<any>> {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournamentId}/prize/pool/${prizePoolId}/winner`));
    }

    claimTournamentEditionPhysicalPrizePool(tournamentId: string, prizePoolId: string, claimCode: string): Promise<SanitizedResponse<any>> {
        return this.report(this.transport.put(`/v1/tournament/edition/${tournamentId}/prize/pool/${prizePoolId}/claim`, {
            claim_code: claimCode
        }));
    }

    // notifications

    queryNotifications(params: NoseurObject<string | number | boolean | undefined> = { page: 1, size: 10 }): Promise<SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>> {
        return this.report(this.transport.get("/v1/tournament/notification", { query: this.normalizeParams(params) }), ({ sanitized }: SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>) => {
            sanitized.grouped_notifications = Notifier.groupNotifications(sanitized.content!);
        });
    }

    markAllNotificationsAsRead(tournamentId: string): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.put(`/v1/tournament/notification/${tournamentId}/read_all`, {}));
    }

    updateNotificationStatus(id: string, status = "READ"): Promise<SanitizedResponse<void>> {
        return this.report(this.transport.patch(`/v1/tournament/notification/${id}`, { status }));
    }

    // register for tournament

    validateIfUserCanRegister(tournamentId: string) {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentId}/registration/validation/pre`));
    }

    queryTournamentEditionRegistereds(params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionRegistered>> {
        return this.report(this.transport.get("/v1/tournament/edition/registered", { query: this.normalizeParams(params) }), ({ content }: SanitizedResponse<ITournamentEditionRegistered>) => {
            content.forEach((c) => this.resolveTournamentFields.bind(this)(c.tournament_edition));
        });
    }

    getSingleTournamentEditionRegistered(id: string) {
        return this.report(this.transport.get(`/v1/tournament/edition/registered/${id}`), ({ sanitized }: SanitizedResponse<ITournamentEditionRegistered>) => {
            this.resolveTournamentFields.bind(this)(sanitized.tournament_edition);
        });
    }

    triggerTournamentEditionRegistration(registeredTournament: Partial<ITournamentEditionRegistered>): Promise<SanitizedResponse<ITournamentEditionRegistered>> {
        return this.report(this.transport.post(`/v1/tournament/edition/registered`, registeredTournament), ({ sanitized }: SanitizedResponse<ITournamentEditionRegistered>) => {
            this.resolveTournamentFields.bind(this)(sanitized.tournament_edition);
        });
    }

    getTournamentEditionRegistrationFee(id: string, currency: string) {
        return this.report(this.transport.get(`/v1/tournament/edition/registered/${id}/fees/${currency}`));
    }

    addParticipantsToTournamentEditionRegistration(id: string, payload: any) {
        return this.report(this.transport.post(`/v1/tournament/edition/registered/${id}/participants`, payload));
    }

    processTournamentEditionRegistrationPayment(id: string, currency: string) {
        const queryDelimiter = window.location.href.includes("?") ? "&" : "?";
        return this.report(this.transport.post(`/v1/tournament/edition/registered/${id}/payment/${currency}`, {
            success_redirect_url: window.location.href + queryDelimiter + "regstatus=success",
            failure_redirect_url: window.location.href + queryDelimiter + "?&regstatus=failed",
        }));
    }

    withdrawTournamentEditionRegistered(id: string, reason: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/registered/${id}/withdraw`, { data: { reason } }));
    }

    removeTournamentEditionRegistered(id: string, reason: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/registered/${id}/remove`, { data: { reason } }));
    }

    sendTournamentEditionRegisteredGift(id: string, currency: string, amount: number) {
        return this.report(this.transport.post(`/v1/tournament/edition/registered/${id}/gift`, {
            amount,
            currency_code: currency
        }));
    }

    queryRegisteredNotifications(params: NoseurObject<string | number | undefined> = { page: 1, size: 10 }): Promise<SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params["registeredTournament.external_id"] === "2"
                    ? {
                        size: 10,
                        total_pages: 1,
                        number_of_elements: 0,
                        content: [],
                    }
                    : {
                        size: 10,
                        total_pages: 1,
                        number_of_elements: TournamentService.TEST_REGISTERED_TOURNAMENT_NOTIFICATIONS.length,
                        content: TournamentService.TEST_REGISTERED_TOURNAMENT_NOTIFICATIONS,
                    })
            }
        }, ({ sanitized }: any) => {
            sanitized.grouped_notifications = Notifier.groupNotifications(sanitized.content!);
        });
        return this.report(this.transport.get("/v1/team/notification", { query: this.normalizeParams(params) }), ({ sanitized }: SanitizedResponse<ApiResponseData<INotification> & { grouped_notifications: NotificationGroup }>) => {
            sanitized.grouped_notifications = Notifier.groupNotifications(sanitized.content!);
        });
    }

    queryRegisteredTournamentMatches(tournamentEditionId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionMatch>> {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentEditionId}/match`, { query: this.normalizeParams({ ...params }) }));
    }

    queryTournamentEditionRegisteredParticipants(tournamentEditionId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionRegisteredParticipant>> {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentEditionId}/registered/participant`, { query: this.normalizeParams(params) }));
    }

    // matches and scoring

    queryTournamentEditionMatches(tournamentId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionMatch>> {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentId}/match`, { query: this.normalizeParams({ ...params, sort: "id,ASC" }) }));
    }

    createTournamentEditionMatch(tournamentId: string, match: Partial<ITournamentEditionMatch>): Promise<SanitizedResponse<ITournamentEditionMatch>> {
        return this.report(this.transport.post(`/v1/tournament/edition/${tournamentId}/match`, match));
    }

    updateTournamentEditionMatch(tournamentId: string, match: Partial<ITournamentEditionMatch>): Promise<SanitizedResponse<ITournamentEditionMatch>> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${tournamentId}/match/${match.external_id}`, match));
    }

    submitTournamentEditionMatchResult(tournamentEditionId: string, tournamentEditionMatchId: string, payload: any) {
        return this.report(this.transport.post(`/v1/tournament/edition/${tournamentEditionId}/match/${tournamentEditionMatchId}/submit`, payload));
    }

    deleteTournamentEditionMatch(tournmamentEditionId: string, id: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournmamentEditionId}/match/${id}`));
    }

    cancelTournamentEditionMatch(tournmamentEditionId: string, id: string, reason: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournmamentEditionId}/match/${id}/cancel`, {
            data: {
                reason,
            }
        }));
    }

    forfeitTournamentEditionMatch(tournmamentEditionId: string, id: string, reason: string, participantId: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournmamentEditionId}/match/${id}/forfeit`, {
            data: {
                reason,
                tournament_edition_registered_participant_id: participantId
            }
        }));
    }

    endTournamentEditionMatch(tournmamentEditionId: string, id: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournmamentEditionId}/match/${id}/end`));
    }

    scoreTournamentEditionMatchResult(tournamentEditionId: string, tournamentEditionMatchId: string, payload: any) {
        return this.report(this.transport.post(`/v1/tournament/edition/${tournamentEditionId}/match/${tournamentEditionMatchId}/score`, payload));
    }

    queryTournamentEditionScorings(tournamentId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionScoring>> {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentId}/scoring`, { query: this.normalizeParams({ ...params, sort: "id,ASC" }) }));
    }

    deleteTournamentEditionScoring(tournmamentEditionId: string, id: string) {
        return this.report(this.transport.delete(`/v1/tournament/edition/${tournmamentEditionId}/scoring/${id}`));
    }

    queryTournamentEditionScoringRecords(tournamentEditionId: string, params: NoseurObject<any> = { page: 1, size: 1000 }) {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentEditionId}/scoring/record`, { query: this.normalizeParams(params) }));
    }

    updateTournamentEditionScoreRecording(tournmamentEditionId: string, id: string, payload: any) {
        return this.report(this.transport.put(`/v1/tournament/edition/${tournmamentEditionId}/scoring/${id}/record`, payload));
    }

    queryMatchAnalytics(params: NoseurObject<string | number> = {}): Promise<any> {
        const { supplementParams, graphDataKey, labelKey } = this.getIdealStatQueryForDateRange(params.from as string, params.to as string);
        return this.report(this.transport.get(`/v1/member/analytics/matches/stat`, { query: this.normalizeParams({ ...params, ...supplementParams }) }), (response: SanitizedResponse<any>) => {
            const graphData = response.sanitized[graphDataKey];
            response.apiResponse.data = {
                labels: graphData.map((g: any) => g[labelKey]),
                datasets: [
                    {
                        label: i18nManager.Labels.host.settings.manage_tournaments.statuses_labels.upcoming,
                        data: graphData.map((g: any) => g["top_status_count"]["UPCOMING"])
                    },
                    {
                        label: i18nManager.Labels.host.settings.manage_tournaments.statuses_labels.ongoing,
                        data: graphData.map((g: any) => g["top_status_count"]["ONGOING"])
                    },
                    {
                        label: i18nManager.Labels.host.settings.manage_tournaments.statuses_labels.ended,
                        data: graphData.map((g: any) => g["top_status_count"]["ENDED"])
                    },
                    {
                        label: i18nManager.Labels.host.settings.manage_tournaments.statuses_labels.cancelled,
                        data: graphData.map((g: any) => g["top_status_count"]["CANCELLED"])
                    },
                    {
                        label: i18nManager.Labels.host.settings.manage_tournaments.statuses_labels.forfeited,
                        data: graphData.map((g: any) => g["top_status_count"]["FORFEITED"])
                    },
                ],
            };
        });
    }

    // prize pool

    queryPrizePoolActivities(tournamentId: string, params: NoseurObject<any> = { page: 1, size: 10 }) {
        return this.report(this.transport.get(`/v1/tournament/edition/${tournamentId}/prize/pool/activity`, { query: this.normalizeParams(params) }));
    }

    announceResults(tournmamentEditionId: string) {
        return this.report(this.transport.put(`/v1/tournament/edition/${tournmamentEditionId}/result/announce`, null));
    }

    distributePrizes(tournmamentEditionId: string) {
        return this.report(this.transport.put(`/v1/tournament/edition/${tournmamentEditionId}/prize/pool/distribute`, null));
    }

    // finance & analytics

    queryCurrencies(tournamentId: string, params: NoseurObject<string | number> = { page: 1, size: 10 }): Promise<any> {
        return this.report(this.transport.get(`/v1/tournament/${tournamentId}/currency/balance`, { query: this.normalizeParams(params) }));
    }

    allocateCurrencyBalance(tournamentId: string, currency: string, amount: number): Promise<any> {
        const queryDelimiter = window.location.href.includes("?") ? "&" : "?";
        return this.report(this.transport.post(`/v1/tournament/${tournamentId}/currency/balance/${currency}/allocate`, {
            amount,
            failure_redirect_url: window.location.href + queryDelimiter + `currency=${currency}&amount=${amount}&allostatus=failed`,
            success_redirect_url: window.location.href + queryDelimiter + `currency=${currency}&amount=${amount}&allostatus=success`,
        }));
    }

    transferCurrencyBalance(tournamentId: string, currency: string, amount: number): Promise<any> {
        return this.report(this.transport.post(`/v1/tournament/${tournamentId}/currency/balance/transfer`, {
            amount,
            currency,
        }));
    }

    queryCurrencyAnalytics(tournamentId: string, params: NoseurObject<string | number> = {}): Promise<any> {
        const { supplementParams, graphDataKey, labelKey } = this.getIdealStatQueryForDateRange(params.from as string, params.to as string);
        return this.report(this.transport.get(`/v1/tournament/${tournamentId}/transaction/stat`, { query: this.normalizeParams({ ...params, ...supplementParams }) }), (response: SanitizedResponse<any>) => {
            const graphData = response.sanitized[graphDataKey];
            response.apiResponse.data = {
                labels: graphData.map((g: any) => g[labelKey]),
                datasets: [
                    {
                        label: "Debit",
                        data: graphData.map((g: any) => ((g["payable_amount_debited"] ?? 0) / 1000000))
                    },
                    {
                        label: "Credit",
                        data: graphData.map((g: any) => ((g["payable_amount_credited"] ?? 0) / 1000000))
                    }
                ],
            };
        });
    }

    queryTransactions(tournamentId: string, params: NoseurObject<string | number | any[]> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentTransaction>> {
        return this.report(this.transport.get(`/v1/tournament/${tournamentId}/transaction`, { query: this.normalizeParams(params) }));
    }

    getTournamentAnalytics(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionAnalytics>> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: (params.currency_code === "KES"
                    ? {
                        matches: {
                            total: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            tournament: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            merchandise: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            advertisement: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        },
                        earnings: {
                            total: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            tournament: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            merchandise: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            advertisement: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        },
                        participants: {
                            total: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            tournament: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            merchandise: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            advertisement: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        },
                        prize_pool: {
                            total: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            tournament: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            merchandise: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                            advertisement: {
                                count: 0,
                                value: 0,
                                percentage: 0,
                            },
                        },
                    } as ITournamentEditionAnalytics
                    : {
                        matches: {
                            total: {
                                count: 20,
                                value: 20,
                                percentage: 10.0,
                            },
                            ended: {
                                count: 10,
                                value: 10,
                                percentage: -3.0,
                            },
                            upcoming: {
                                count: 8,
                                value: 8,
                                percentage: 0,
                            },
                            ongoing: {
                                count: 2,
                                value: 2,
                                percentage: -20,
                            },
                        },
                        earnings: {
                            total: {
                                count: 30,
                                value: 55600000,
                                percentage: 19.83,
                            },
                            registration: {
                                count: 10,
                                value: 20454000,
                                percentage: -10.4,
                            },
                            merchandise: {
                                count: 6,
                                value: 20000000,
                                percentage: 98.5,
                            },
                            advertisement: {
                                count: 4,
                                value: 30000000,
                                percentage: 0.0,
                            },
                        },
                        participants: {
                            new: {
                                count: 200,
                                value: 200,
                                percentage: -20.2,
                            },
                            total: {
                                count: 1000,
                                value: 1000,
                                percentage: 2.9,
                            },
                            removed: {
                                count: 50,
                                value: 50,
                                percentage: 0,
                            },
                            withdrawn: {
                                count: 700,
                                value: 700,
                                percentage: 0.12,
                            },
                        },
                        prize_pool: {
                            total: {
                                count: 200,
                                value: 200,
                                percentage: 81.01,
                            },
                            trophy: {
                                count: 201,
                                value: 201,
                                percentage: 0,
                            },
                            monetary: {
                                count: 75,
                                value: 56000000,
                                percentage: -9.27,
                            },
                            physical: {
                                count: 30,
                                value: 30,
                                percentage: 0.01,
                            },
                        },
                    } as ITournamentEditionAnalytics),
            }
        });
        return this.report(this.transport.get(`/v1/team/${hostId}/analytics`, { query: this.normalizeParams(params) }));
    }

    queryAnalyticsChartData(hostId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<any> {
        return this.mockResolve(1000, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    labels: Array(7).fill(0).map((_, i) => i + 1),
                    datasets: [
                        {
                            label: "earnings",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(50000, 500000))
                        },
                        {
                            label: "tournaments",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(20, 30000))
                        },
                        {
                            label: "programs",
                            data: Array(7).fill(0).map((_, i) => Utils.randomInt(0, 20000))
                        },
                    ]
                }
            }
        }, ({ apiResponse }: SanitizedResponse<any>) => {
            apiResponse.data!.datasets = apiResponse.data!.datasets.map((d: any) => ({
                data: d.data,
                label: (i18nManager.Labels.host.analytics.chart_labels as any)[d.label] ?? d.label,
            }))
        });
        return this.report(this.transport.get(`/v1/team/${hostId}/analytics`, { query: this.normalizeParams(params) }));
    }

    // settings

    getTournamentEditionWebsite(editionId: string): Promise<SanitizedResponse<ITournamentEditionWebsite>> {
        return this.report(this.transport.get(`/v1/tournament/edition/${editionId}/website`));
    }

    updateTournamentEditionWebsite(editionId: string, tournamentEditionWebsite: ITournamentEditionWebsite): Promise<SanitizedResponse<ITournamentEditionWebsite>> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${editionId}/website`, tournamentEditionWebsite));
    }

    queryTournamentEditionEvents(editionId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionEvent>> {
        params["tournament_edition.external_id"] = editionId; // TODO FIX IN KYOFUUC
        return this.report(this.transport.get(`/v1/tournament/edition/${editionId}/website/event`, { query: this.normalizeParams(params) }));
    }

    createTournamentEditionEvent(editionId: string, event: Partial<ITournamentEditionEvent>): Promise<any> {
        return this.report(this.transport.post(`/v1/tournament/edition/${editionId}/website/event`, event));
    }

    updateTournamentEditionEvent(editionId: string, event: Partial<ITournamentEditionEvent>): Promise<any> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${editionId}/website/event/${event.external_id}`, event));
    }

    deleteTournamentEditionEvent(editionId: string, event: Partial<ITournamentEditionEvent>): Promise<any> {
        return this.report(this.transport.delete(`/v1/tournament/edition/${editionId}/website/event/${event.external_id}`));
    }

    getGallery(editionId: string, params: NoseurObject<any> = { page: 1, size: 100 }): Promise<SanitizedResponse<IGallery>> {
        return this.report(this.transport.get(`/v1/tournament/edition/${editionId}/website/gallery`, { query: this.normalizeParams(params) }));
    }

    createGallery(editionId: string, gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.post(`/v1/tournament/edition/${editionId}/website/gallery`, gallery));
    }

    updateGallery(editionId: string, gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${editionId}/website/gallery/${gallery.external_id}`, gallery));
    }

    deleteGallery(editionId: string, gallery: Partial<IGallery>): Promise<any> {
        return this.report(this.transport.delete(`/v1/tournament/edition/${editionId}/website/gallery/${gallery.external_id}`));
    }

    queryTournamentEditionFaqs(faqId: string, params: NoseurObject<any> = { page: 1, size: 10 }): Promise<SanitizedResponse<ITournamentEditionFaqs>> {
        params["tournament_edition.external_id"] = faqId; // TODO FIX IN KYOFUUC
        return this.report(this.transport.get(`/v1/tournament/edition/${faqId}/website/faq`, { query: this.normalizeParams(params) }));
    }

    createTournamentEditionFaq(faqId: string, faq: Partial<ITournamentEditionFaqs>): Promise<any> {
        return this.report(this.transport.post(`/v1/tournament/edition/${faqId}/website/faq`, faq));
    }

    updateTournamentEditionFaq(faqId: string, faq: Partial<ITournamentEditionFaqs>): Promise<any> {
        return this.report(this.transport.patch(`/v1/tournament/edition/${faqId}/website/faq/${faq.external_id}`, faq));
    }

    deleteTournamentEditionFaq(faqId: string, faq: Partial<ITournamentEditionFaqs>): Promise<any> {
        return this.report(this.transport.delete(`/v1/tournament/edition/${faqId}/website/faq/${faq.external_id}`));
    }

    static TEST_CURRENCIES: ITournamentCurrencyBalance[] = [
        {
            external_id: "1",
            balance_amount: 509960000,
            currency_code: "NGN",
            tournament_id: "1000",
            transferred_amount: 30000,
            percentage_increase: 5.20,
            projected_return_amount: 460000,
            manually_allocated_amount: 7892782,
        },
        {
            external_id: "2",
            balance_amount: 10007880,
            currency_code: "INR",
            tournament_id: "1000",
            transferred_amount: 30000,
            percentage_increase: -2.90,
            projected_return_amount: 460000,
            manually_allocated_amount: 7892782,
        },
        {
            external_id: "3",
            balance_amount: 12232023,
            currency_code: "USD",
            tournament_id: "1000",
            transferred_amount: 30000,
            percentage_increase: 0.00,
            projected_return_amount: 460000,
            manually_allocated_amount: 7892782,
        },
    ];

    static TEST_TRANSACTIONS: any[] = [
        {
            tax: 2000,
            amount: 200000,
            external_id: "1",
            tax_percentage: 1,
            currency_code: "NGN",
            amount_received: 900000,
            platform_charge_fee: 10000,
            type: TransactionType.CREDIT,
            status: TransactionStatus.PENDING,
            platform_charge_fee_percentage: 10,
            description: "Winning - First Prize",
            date: "2025-02-21T19:37:18.000+00:00",
            source: {
                id: "1",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[0].name,
                extra_data: {
                    public_id: "1",
                    //logo: TournamentTestService.TEST_TOURNAMENTS[0].logo_urls[0],
                }
            }
        },
        {
            tax: 0,
            amount: 20000000,
            external_id: "2",
            tax_percentage: 2,
            currency_code: "NGN",
            amount_received: 56500000,
            platform_charge_fee: 65000,
            type: TransactionType.CREDIT,
            status: TransactionStatus.PENDING,
            platform_charge_fee_percentage: 16,
            description: "Tournament Host Earning",
            date: "2025-02-30T19:37:18.000+00:00",
            source: {
                id: "2",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[1].name,
                extra_data: {
                    public_id: "2",
                    name: TournamentTestService.TEST_TOURNAMENTS[1].name,
                    //logo: TournamentTestService.TEST_TOURNAMENTS[1].logo_urls[0],
                }
            }
        },
        {
            tax: 3400,
            amount: 540000,
            external_id: "3",
            tax_percentage: 0,
            currency_code: "GHS",
            amount_received: 872736,
            platform_charge_fee: 25523,
            type: TransactionType.CREDIT,
            platform_charge_fee_percentage: 9,
            status: TransactionStatus.PENDING,
            date: "2025-04-01T19:37:18.000+00:00",
            description: "Tournament Host Earning",
            source: {
                id: "2",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[2].name,
                extra_data: {
                    public_id: "1",
                    name: TournamentTestService.TEST_TOURNAMENTS[2].name,
                    //logo: TournamentTestService.TEST_TOURNAMENTS[2].logo_urls[0],
                }
            }
        },
        {
            tax: 1400,
            amount: 9273100,
            external_id: "4",
            tax_percentage: 0,
            currency_code: "USD",
            platform_charge_fee: 0,
            amount_received: 9273100,
            type: TransactionType.CREDIT,
            platform_charge_fee_percentage: 0,
            status: TransactionStatus.AVAILABLE,
            date: "2025-05-10T19:37:18.000+00:00",
            description: "Tournament Host Earning",
            source: {
                id: "3",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[3].name,
                extra_data: {
                    public_id: "3",
                    name: TournamentTestService.TEST_TOURNAMENTS[3].name,
                    //logo: TournamentTestService.TEST_TOURNAMENTS[3].logo_urls[0],
                }
            }
        },
        {
            tax: 56900,
            amount: 902772,
            external_id: "5",
            tax_percentage: 10,
            currency_code: "NGN",
            amount_received: 902772,
            description: "Withdrawal",
            platform_charge_fee: 40090,
            type: TransactionType.WITHDRAWAL,
            platform_charge_fee_percentage: 10,
            status: TransactionStatus.AVAILABLE,
            date: "2025-05-11T19:37:18.000+00:00",
            source: {
                id: "1",
                type: TransactionSourceType.WITHDRAWAL,
                //label: EarningService.TEST_MEMBER_PAYOUT_METHODS[0].label,
            },
        },
        {
            tax: 56900,
            amount: 200000,
            external_id: "6",
            tax_percentage: 9,
            currency_code: "INR",
            amount_received: 900000,
            platform_charge_fee: 10000,
            type: TransactionType.CREDIT,
            platform_charge_fee_percentage: 10,
            status: TransactionStatus.AVAILABLE,
            description: "Winning - First Prize",
            date: "2025-02-21T19:37:18.000+00:00",
            source: {
                id: "1",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[0].name,
                extra_data: {
                    public_id: "1",
                    //logo: TournamentTestService.TEST_TOURNAMENTS[0].logo_urls[0],
                }
            }
        },
        {
            tax: 56900,
            amount: 20000000,
            external_id: "7",
            tax_percentage: 0,
            currency_code: "KES",
            amount_received: 56500000,
            platform_charge_fee: 65000,
            type: TransactionType.CREDIT,
            platform_charge_fee_percentage: 16,
            status: TransactionStatus.AVAILABLE,
            description: "Tournament Host Earning",
            date: "2025-02-30T19:37:18.000+00:00",
            source: {
                id: "2",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[1].name,
                extra_data: {
                    public_id: "2",
                    name: TournamentTestService.TEST_TOURNAMENTS[1].name,
                    //logo: TournamentTestService.TEST_TOURNAMENTS[1].logo_urls[0],
                }
            }
        },
        {
            tax: 90900,
            amount: 540000,
            external_id: "8",
            tax_percentage: 0,
            currency_code: "ZAR",
            amount_received: 872736,
            platform_charge_fee: 25523,
            type: TransactionType.CREDIT,
            platform_charge_fee_percentage: 9,
            status: TransactionStatus.PENDING,
            description: "Tournament Host Earning",
            date: "2025-04-01T19:37:18.000+00:00",
            source: {
                id: "2",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[2].name,
                extra_data: {
                    public_id: "1",
                    name: TournamentTestService.TEST_TOURNAMENTS[2].name,
                    //logo: TournamentTestService.TEST_TOURNAMENTS[2].logo_urls[0],
                }
            }
        },
        {
            tax: 90900,
            amount: 902772,
            external_id: "8",
            tax_percentage: 0,
            currency_code: "NGN",
            amount_received: 902772,
            description: "Withdrawal",
            platform_charge_fee: 40090,
            type: TransactionType.WITHDRAWAL,
            status: TransactionStatus.PENDING,
            platform_charge_fee_percentage: 10,
            date: "2025-05-11T19:37:18.000+00:00",
            source: {
                id: "1",
                type: TransactionSourceType.WITHDRAWAL,
                //label: EarningService.TEST_MEMBER_PAYOUT_METHODS[1].label,
            },
        },
        {
            tax: 90900,
            amount: 9273100,
            external_id: "10",
            tax_percentage: 8,
            currency_code: "USD",
            platform_charge_fee: 0,
            amount_received: 9273100,
            description: "Annual Gift",
            type: TransactionType.CREDIT,
            status: TransactionStatus.PENDING,
            platform_charge_fee_percentage: 0,
            date: "2025-05-10T19:37:18.000+00:00",
            source: {
                id: "3",
                label: "OmneHost",
                type: TransactionSourceType.GIFT,
                extra_data: {
                    public_id: "3",
                    logo: "https://avatars.githubusercontent.com/u/65621284?s=200&v=4",
                }
            }
        },
        {
            tax: 90900,
            amount: 902772,
            external_id: "11",
            tax_percentage: 4,
            currency_code: "NGN",
            amount_received: 902772,
            description: "Withdrawal",
            platform_charge_fee: 40090,
            type: TransactionType.WITHDRAWAL,
            status: TransactionStatus.PENDING,
            platform_charge_fee_percentage: 10,
            date: "2025-05-11T19:37:18.000+00:00",
            source: {
                id: "1",
                type: TransactionSourceType.WITHDRAWAL,
                //label: EarningService.TEST_MEMBER_PAYOUT_METHODS[0].label,
            },
        },
        {
            tax: 90900,
            amount: 20000000,
            external_id: "12",
            tax_percentage: 12,
            currency_code: "KES",
            amount_received: 56500000,
            platform_charge_fee: 65000,
            type: TransactionType.CREDIT,
            platform_charge_fee_percentage: 16,
            status: TransactionStatus.WITHDRAWN,
            description: "Tournament Host Earning",
            date: "2025-02-30T19:37:18.000+00:00",
            source: {
                id: "2",
                type: TransactionSourceType.TOURNAMENT_REGISTRATION,
                label: TournamentTestService.TEST_TOURNAMENTS[1].name,
                extra_data: {
                    public_id: "2",
                    name: TournamentTestService.TEST_TOURNAMENTS[1].name,
                    //logo: TournamentTestService.TEST_TOURNAMENTS[1].logo_urls[0],
                }
            }
        },
        {
            tax: 0,
            amount: 9273100,
            external_id: "10",
            tax_percentage: 10,
            currency_code: "USD",
            platform_charge_fee: 0,
            amount_received: 9273100,
            description: "Rivtn Ads",
            type: TransactionType.CREDIT,
            platform_charge_fee_percentage: 0,
            status: TransactionStatus.WITHDRAWN,
            date: "2025-06-10T19:37:18.000+00:00",
            source: {
                id: "3",
                label: "Rivtn Ads",
                type: TransactionSourceType.ADVERTISEMENT
            }
        },
        {
            tax: 56900,
            amount: 902772,
            external_id: "11",
            tax_percentage: 10,
            currency_code: "NGN",
            amount_received: 902772,
            platform_charge_fee: 40090,
            type: TransactionType.WITHDRAWAL,
            description: "Auto - Withdrawal",
            platform_charge_fee_percentage: 10,
            status: TransactionStatus.AVAILABLE,
            date: "2025-05-11T19:37:18.000+00:00",
            source: {
                id: "1",
                type: TransactionSourceType.WITHDRAWAL,
                //label: EarningService.TEST_MEMBER_PAYOUT_METHODS[0].label,
            },
        },
    ];

    static TEST_CUSTOM_FIELDS: ITournamentEditionCustomField[] = [
        {
            required: false,
            external_id: "1",
            label: "Why do you want to play?",
            type: TournamentCustomFieldType.TEXT,
        },
        {
            required: true,
            external_id: "2",
            type: TournamentCustomFieldType.TEXT,
            label: "How many time have you plated Warhammer?",
        },
        {
            required: true,
            external_id: "3",
            label: "Do you like Gaming",
            type: TournamentCustomFieldType.OPTIONS,
            options: [
                "No",
                "Yes",
            ],
        },
        {
            required: true,
            external_id: "4",
            type: TournamentCustomFieldType.OPTIONS,
            label: "Will you be interested in getting our merchandise?",
            options: [
                "No",
                "Yes",
                "Maybe",
            ],
        },
        {
            required: true,
            external_id: "5",
            type: TournamentCustomFieldType.CHECK,
            label: "Are you open to new tournament newsletter",
        },
        {
            required: true,
            external_id: "6",
            type: TournamentCustomFieldType.CHECK,
            label: "I agree to the tournament rules",
        },
        {
            required: true,
            external_id: "7",
            label: "How old are you",
            type: TournamentCustomFieldType.NUMBER,
        },
        {
            required: true,
            external_id: "8",
            label: "What is your date of birth",
            type: TournamentCustomFieldType.DATE,
        },
    ];

    static TEST_REGISTERED_TOURNAMENTS: any[] = [
        {
            pass_id: "1",
            seed_number: 1,
            external_id: "1",
            status: RegisteredTournamentStatus.REGISTERED,
            registration_date: "2025-09-22T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[1],
            resource: {
                name: "The Royals",
                logo_url: "http://127.0.0.1:9305/api/v1/storage/o/user/omnehost/member/profile/pro_pic_bae6b85e-9a03-4c"
            },
            custom_fields_answers: {
                one: "No",
                three: 19,
                two: "thegamerGod",
            },
            custom_fields_id_map: {
                one: {
                    required: false,
                    external_id: "one",
                    label: "Aye you gay?",
                    type: TournamentCustomFieldType.OPTIONS,
                    options: [
                        "Yes",
                        "No"
                    ]
                },
                two: {
                    required: true,
                    external_id: "two",
                    label: "What is your Player Tag",
                    type: TournamentCustomFieldType.TEXT
                },
                three: {
                    required: true,
                    external_id: "three",
                    label: "How old are you",
                    type: TournamentCustomFieldType.NUMBER
                },
            },
            registration_amount: {
                amount: 0,
                charge_fee: 0,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
                color_scheme: {
                    bg: "Red"
                }
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 2
            },
            own_standings: {
                rank: 3,
                total_rank: 40,
            },
            own_matches_detail: {
                matches_won: 1,
                matches_lost: 40,
                matches_left: 12,
                matches_total: 64,
                tournament_total_matches: 90,
            },
        },
        {
            pass_id: "2",
            seed_number: 2,
            external_id: "2",
            status: RegisteredTournamentStatus.WITHDRAWN,
            registration_date: "2025-09-22T12:08:39.000+00:00",
            withdrawal_removal_date: "2025-09-30T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[1],
            resource: {
                name: "The Royals",
                logo_url: "http://127.0.0.1:9305/api/v1/storage/o/user/omnehost/member/profile/pro_pic_bae6b85e-9a03-4c"
            },
            custom_fields_answers: {
                one: "No",
                three: 19,
                two: "thegamerGod",
            },
            custom_fields_id_map: {
                one: {
                    required: false,
                    external_id: "one",
                    label: "Aye you gay?",
                    type: TournamentCustomFieldType.OPTIONS,
                    options: [
                        "Yes",
                        "No"
                    ]
                },
                two: {
                    required: true,
                    external_id: "two",
                    label: "What is your Player Tag",
                    type: TournamentCustomFieldType.TEXT
                },
                three: {
                    required: true,
                    external_id: "three",
                    label: "How old are you",
                    type: TournamentCustomFieldType.NUMBER
                },
            },
            registration_amount: {
                amount: 1000000,
                charge_fee: 100000,
                currency_code: "NGN",
            },
            refund_amount: {
                amount: 1000000,
                charge_fee: 100000,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
                color_scheme: {
                    bg: "Red"
                }
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 2
            },
            own_standings: {
                rank: 3,
                total_rank: 40,
            },
            own_matches_detail: {
                matches_won: 1,
                matches_lost: 40,
                matches_left: 12,
                matches_total: 64,
                tournament_total_matches: 90,
            },
        },
        {
            pass_id: "3",
            seed_number: 3,
            external_id: "3",
            status: RegisteredTournamentStatus.REMOVED,
            registration_date: "2025-09-22T12:08:39.000+00:00",
            withdrawal_removal_date: "2025-09-30T12:08:39.000+00:00",
            tournament: TournamentTestService.TEST_TOURNAMENTS[1],
            resource: {
                name: "The Royals",
                logo_url: "http://127.0.0.1:9305/api/v1/storage/o/user/omnehost/member/profile/pro_pic_bae6b85e-9a03-4c"
            },
            custom_fields_answers: {
                one: "No",
                three: 19,
                two: "thegamerGod",
            },
            custom_fields_id_map: {
                one: {
                    required: false,
                    external_id: "one",
                    label: "Aye you gay?",
                    type: TournamentCustomFieldType.OPTIONS,
                    options: [
                        "Yes",
                        "No"
                    ]
                },
                two: {
                    required: true,
                    external_id: "two",
                    label: "What is your Player Tag",
                    type: TournamentCustomFieldType.TEXT
                },
                three: {
                    required: true,
                    external_id: "three",
                    label: "How old are you",
                    type: TournamentCustomFieldType.NUMBER
                },
            },
            registration_amount: {
                amount: 0,
                charge_fee: 0,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
                color_scheme: {
                    bg: "Red"
                }
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 2
            },
            own_standings: {
                rank: 3,
                total_rank: 40,
            },
            own_matches_detail: {
                matches_won: 1,
                matches_lost: 40,
                matches_left: 12,
                matches_total: 64,
                tournament_total_matches: 90,
            },
        },
        {
            pass_id: "4",
            external_id: "4",
            use_banner_background: true,
            status: RegisteredTournamentStatus.REGISTERED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[4],
            registration_amount: {
                amount: 0,
                charge_fee: 0,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 43
            },
            own_standings: {
                rank: 14,
                total_rank: 31,
            },
            own_matches_detail: {
                matches_won: 1,
                matches_lost: 0,
                matches_left: 0,
                matches_total: 0,
                tournament_total_matches: 1,
            },
        },
        {
            pass_id: "5",
            external_id: "5",
            use_banner_background: true,
            status: RegisteredTournamentStatus.WITHDRAWN,
            tournament: TournamentTestService.TEST_TOURNAMENTS[5],
            withdrawal_reason: "I am withdrawing because all my team members turned on me and I cannot participate by my own self so I had to remove myself from the tournament this sadden me but what can I do",
            registration_amount: {
                amount: 100000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            refund_amount: {
                amount: 90000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 1
            },
            own_standings: {
                rank: 2,
                total_rank: 234,
            },
            own_matches_detail: {
                matches_won: 1,
                matches_lost: 40,
                matches_left: 12,
                matches_total: 64,
                tournament_total_matches: 90,
            },
        },
        {
            pass_id: "6",
            external_id: "6",
            status: RegisteredTournamentStatus.REGISTERED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[6],
            registration_amount: {
                amount: 100000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 3
            },
            own_standings: {
                rank: 234,
                total_rank: 234,
            },
            own_matches_detail: {
                matches_won: 10,
                matches_lost: 10,
                matches_left: 2,
                matches_total: 4,
                tournament_total_matches: 19,
            },
        },
        {
            pass_id: "7",
            external_id: "7",
            status: RegisteredTournamentStatus.REGISTERED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[7],
            registration_amount: {
                amount: 100000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            own_standings: {
                rank: 3,
                total_rank: 10,
            },
            own_matches_detail: {
                matches_won: 109,
                matches_lost: 120,
                matches_left: 29,
                matches_total: 77,
                tournament_total_matches: 199,
            },
        },
        {
            pass_id: "8",
            external_id: "8",
            status: RegisteredTournamentStatus.REGISTERED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[8],
            registration_amount: {
                amount: 100000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 109
            },
            own_standings: {
                rank: 1,
                total_rank: 2,
            },
            own_matches_detail: {
                matches_won: 10,
                matches_lost: 10,
                matches_left: 2,
                matches_total: 4,
                tournament_total_matches: 19,
            },
        },
        {
            pass_id: "9",
            external_id: "9",
            status: RegisteredTournamentStatus.REGISTERED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[9],
            registration_amount: {
                amount: 100000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 1
            },
            own_standings: {
                rank: 353,
                total_rank: 13443,
            },
            own_matches_detail: {
                matches_won: 23,
                matches_lost: 22,
                matches_left: 1,
                matches_total: 55,
                tournament_total_matches: 32,
            },
        },
        {
            pass_id: "10",
            external_id: "10",
            status: RegisteredTournamentStatus.REGISTERED,
            tournament: TournamentTestService.TEST_TOURNAMENTS[10],
            registration_amount: {
                amount: 100000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            un_reads: {
                notifications: 0
            },
            own_standings: {
                rank: 31,
                total_rank: 134,
            },
            own_matches_detail: {
                matches_won: 13,
                matches_lost: 11,
                matches_left: 51,
                matches_total: 2,
                tournament_total_matches: 34,
            },
        },
        {
            pass_id: "11",
            external_id: "11",
            status: RegisteredTournamentStatus.WITHDRAWN,
            tournament: TournamentTestService.TEST_TOURNAMENTS[11],
            withdrawal_reason: "I am withdrawing because all my team members turned on me and I cannot participate by my own self so I had to remove myself from the tournament this sadden me but what can I do",
            registration_amount: {
                amount: 100000,
                charge_fee: 10000,
                currency_code: "NGN",
            },
            refund_amount: {
                amount: 0,
                charge_fee: 0,
                currency_code: "NGN",
            },
            pass_settings: {
                design_id: "1",
            },
            participants: [

            ],
            extra_participants: [

            ],
            own_standings: {
                rank: 1,
                total_rank: 55,
            },
            own_matches_detail: {
                matches_won: 18,
                matches_lost: 1,
                matches_left: 2,
                matches_total: 4,
                tournament_total_matches: 129,
            },
        },
    ];

    static TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS: any[] = [
        {
            pass_id: "1",
            external_id: "1",
            type: RegisteredTournamentParticipantType.TEAM,
            tournament: TournamentTestService.TEST_TOURNAMENTS[1],
            resource: {
                name: "The Royals",
                logo_url: "http://127.0.0.1:9305/api/v1/storage/o/user/omnehost/member/profile/pro_pic_bae6b85e-9a03-4c"
            },
            tournament_edition_registered: TournamentService.TEST_REGISTERED_TOURNAMENTS[0],
        },
        {
            pass_id: "2",
            external_id: "2",
            type: RegisteredTournamentParticipantType.MEMBER,
            tournament: TournamentTestService.TEST_TOURNAMENTS[1],
            resource: {
                name: "Crazy Pandaz",
                logo_url: "http://127.0.0.1:9305/api/v1/storage/o/user/omnehost/member/profile/pro_pic_bae6b85e-9a03-4c"
            },
            tournament_edition_registered: TournamentService.TEST_REGISTERED_TOURNAMENTS[0],
        },
        {
            pass_id: "3",
            external_id: "3",
            type: RegisteredTournamentParticipantType.TEAM,
            tournament: TournamentTestService.TEST_TOURNAMENTS[1],
            resource: {
                name: "Niggaz",
                logo_url: "http://127.0.0.1:9305/api/v1/storage/o/user/omnehost/member/profile/pro_pic_bae6b85e-9a03-4c"
            },
            tournament_edition_registered: TournamentService.TEST_REGISTERED_TOURNAMENTS[1],
        },
        {
            pass_id: "4",
            external_id: "4",
            type: RegisteredTournamentParticipantType.MEMBER,
            tournament: TournamentTestService.TEST_TOURNAMENTS[2],
            resource: {
                name: "Fighther Z",
                logo_url: "http://127.0.0.1:9305/api/v1/storage/o/user/omnehost/member/profile/pro_pic_bae6b85e-9a03-4c"
            },
            tournament_edition_registered: TournamentService.TEST_REGISTERED_TOURNAMENTS[2],
        },
    ];

    static TEST_REGISTERED_TOURNAMENT_NOTIFICATIONS: INotification[] = [
        {
            actions: [],
            external_id: "1",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            created_at: "2025-08-22T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
        {
            actions: [],
            external_id: "2",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://lh3.googleusercontent.com/gLXRlZfIsFY2iTdpnp9bK6fxQPrW-vYtz0BtpSIRRcmYb3DvYyTohDktwLv5Swq6GvNwUjpRb1jjt1c=w544-h544-l90-rj",
            created_at: "2025-09-03T12:08:39.000+00:00",
            message: "A new member ‘vazrak’ just joined the team",
        },
        {
            actions: [],
            external_id: "3",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://music.youtube.com/img/on_platform_logo_dark.svg",
            created_at: "2025-09-03T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
        {
            actions: [],
            external_id: "4",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://avatars.githubusercontent.com/u/65621284?s=200&v=4",
            created_at: "2025-09-21T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
        {
            actions: [],
            external_id: "5",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            created_at: "2025-08-22T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
        {
            actions: [],
            external_id: "6",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://lh3.googleusercontent.com/gLXRlZfIsFY2iTdpnp9bK6fxQPrW-vYtz0BtpSIRRcmYb3DvYyTohDktwLv5Swq6GvNwUjpRb1jjt1c=w544-h544-l90-rj",
            created_at: "2025-09-03T12:08:39.000+00:00",
            message: "A new member ‘vazrak’ just joined the team",
        },
        {
            actions: [],
            external_id: "7",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://music.youtube.com/img/on_platform_logo_dark.svg",
            created_at: "2025-09-03T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
        {
            actions: [],
            external_id: "8",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://avatars.githubusercontent.com/u/65621284?s=200&v=4",
            created_at: "2025-09-21T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
        {
            actions: [],
            external_id: "9",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://lh3.googleusercontent.com/gLXRlZfIsFY2iTdpnp9bK6fxQPrW-vYtz0BtpSIRRcmYb3DvYyTohDktwLv5Swq6GvNwUjpRb1jjt1c=w544-h544-l90-rj",
            created_at: "2025-09-03T12:08:39.000+00:00",
            message: "A new member ‘vazrak’ just joined the team",
        },
        {
            actions: [],
            external_id: "10",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://music.youtube.com/img/on_platform_logo_dark.svg",
            created_at: "2025-09-22T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
        {
            actions: [],
            external_id: "11",
            type: "ACTIVITY",
            title: "Activity",
            status: "UN_READ",
            image_location: "https://avatars.githubusercontent.com/u/65621284?s=200&v=4",
            created_at: "2025-09-22T12:08:39.000+00:00",
            message: "The moderator 'John Does' has register the team for the tournament 'Versus of Kings', The verse is very very initiated",
        },
    ];

    static TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS: any[] = [
        {
            scores: {
                "Points": 10,
                "Goal Difference": 24.5
            },
            resource: {
                is_member: true,
                resource_id: "1",
                name: "Flaming Ninjas",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://i.pinimg.com/736x/45/d1/83/45d183086872b1a137b6fcd6de2a7fef.jpg",
                matches_statistics: {
                    won: 2,
                    lost: 5,
                    draw: 1,
                },
                participants: [
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator and the terminator and the terminatorn and the terminator and the terminator and the terminatorn and the terminator and the terminator and the terminatorn and the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    }
                ]
            }
        },
        {
            scores: {
                "Points": 9,
                "Goal Difference": 29
            },
            resource: {
                is_member: false,
                resource_id: "2",
                name: "Crazy Pandaz",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://upload.wikimedia.org/wikipedia/en/5/5c/Panda_Z.gif",
                scores: {
                    "Score 1": 7,
                    "Score 2": 9
                },
                matches_statistics: {
                    won: 12,
                    lost: 1,
                    draw: 8,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                ]
            }
        },
        {
            resource: {
                is_member: false,
                resource_id: "2",
                name: "Bad Clawz",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://img.freepik.com/free-vector/black-white-illustration-monster-claws-breaking-through-ripping-tearing-scratching-wall_1284-42119.jpg",
                scores: {
                    "Score 1": 95,
                    "Score 2": 21
                },
                matches_statistics: {
                    won: 19,
                    lost: 21,
                    draw: 81,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    }
                ]
            }
        },
        {
            resource: {
                is_member: false,
                resource_id: "3",
                name: "Desperado Eliminators",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://urbanroad.com/cdn/shop/files/UR2387.jpg?v=1719822600",
                scores: {
                    "Score 1": 81,
                    "Score 2": 2
                },
                matches_statistics: {
                    won: 19,
                    lost: 21,
                    draw: 81,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    }
                ]
            }
        },
        {
            resource: {
                is_member: false,
                resource_id: "4",
                name: "Desperados",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://cdn-images.threadless.com/~tms/rock-artwork/products/17150/shirt-1459445835-3481005d4ce6d1a60f2d1313f7d14ec2.png?v=3&d=g6NvcHOWk6R0cmltksLCgJOmcmVzaXplkIOld2lkdGjLQI8gAAAAAACmaGVpZ2h0y0CPIAAAAAAAqGFsbG93X3VwwpOvY2FudmFzX2NlbnRlcmVkks0EsM0EsIGqYmFja2dyb3VuZKZmZmZmZmaTpnJlc2l6ZZHNAyCAk69jYW52YXNfY2VudGVyZWSTzQMgzQMgpyNmZmZmZmaAk6ZlbmNvZGWSpHdlYnBVgKVmb3JjZcKpb25seV9tZXRhwg==&f=mp",
                scores: {
                    "Score 1": 1,
                    "Score 2": 29
                },
                matches_statistics: {
                    won: 21,
                    lost: 12,
                    draw: 102,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    }
                ]
            }
        },
        {
            resource: {
                is_member: false,
                resource_id: "5",
                name: "Regulars Icers",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://i0.wp.com/icesculptingsecrets.com/wp-content/uploads/2020/05/dancing-with-cat.jpg",
                scores: {
                    "Score 1": 76,
                    "Score 2": 44
                },
                matches_statistics: {
                    won: 34,
                    lost: 11,
                    draw: 28,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    }
                ]
            }
        },
        {
            resource: {
                is_member: false,
                resource_id: "6",
                name: "Fire Emitters",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://storage.googleapis.com/pod_public/1300/151322.jpg",
                scores: {
                    "Score 1": 12,
                    "Score 2": 21
                },
                matches_statistics: {
                    won: 133,
                    lost: 1,
                    draw: 45,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    },
                ]
            }
        },
        {
            resource: {
                is_member: false,
                resource_id: "7",
                name: "Olympian Gods",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://img.freepik.com/free-photo/beautiful-clouds-digital-art_23-2151105788.jpg?semt=ais_hybrid&w=740&q=80",
                scores: {
                    "Score 1": 43,
                    "Score 2": 297
                },
                matches_statistics: {
                    won: 133,
                    lost: 1,
                    draw: 45,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    },
                    {
                        username: "Travota",
                        tag_line: "Level Up Your Reality.",
                        profile_picture_url: "https://pictures-nigeria.jijistatic.net/179523839_MTYwMC0xMjAwLTY1YTA5NmM4N2Y.webp"
                    },
                ]
            }
        },
        {
            resource: {
                is_member: false,
                resource_id: "8",
                name: "Bad Batch",
                type: RegisteredTournamentParticipantType.TEAM,
                profile_picture_url: "https://www.syfy.com/sites/syfy/files/2021/05/star-wars-the-bad-batch-key-art-hero.jpg",
                scores: {
                    "Score 1": 11,
                    "Score 2": 22
                },
                matches_statistics: {
                    won: 19,
                    lost: 21,
                    draw: 81,
                },
                participants: [
                    {
                        username: "amitabha",
                        tag_line: "verilly the savious and the cleaner of lives",
                        profile_picture_url: "https://pbs.twimg.com/media/FF3rQb_XwAgLeqT?format=jpg&name=4096x4096"
                    },
                    {
                        username: "ataminahth",
                        tag_line: "the terminator",
                        profile_picture_url: "https://viberate-upload.ams3.cdn.digitaloceanspaces.com/prod/entity/artist/pandaz-tDMYP"
                    },
                    {
                        username: "John",
                        tag_line: "The universal regulator",
                        profile_picture_url: "https://images.ctfassets.net/h6goo9gw1hh6/2sNZtFAWOdP1lmQ33VwRN3/24e953b920a9cd0ff2e1d587742a2472/1-intro-photo-final.jpg?w=1200&h=992&fl=progressive&q=70&fm=jpg"
                    }
                ]
            }
        },
    ];

    static TEST_REGISTERED_TOURNAMENT_MATCHES: any[] = [
        {
            external_id: "1",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 1,
            match_number: 3,
            determining_score: "Score 1",
            location: "Ajegunle Lagos State",
            date: "2025-08-22T12:08:39.000+00:00",
            status: TournamentMatchStatus.UPCOMING,
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[1],
            ]
        },
        {
            external_id: "2",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 2,
            match_number: 4,
            location: "Nuke Town",
            date: "2025-10-22T12:08:39.000+00:00",
            determining_score: "Score 1",
            status: TournamentMatchStatus.ONGOING,
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[2],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
            ]
        },
        {
            external_id: "3",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.RANK,
            },
            group_number: 4,
            match_number: 5,
            location: "Vandansk",
            date: "2025-11-24T12:08:39.000+00:00",
            determining_score: "Score 2",
            status: TournamentMatchStatus.ONGOING,
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[1],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[2],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[3],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[4],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[5],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[6],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[7],
            ]
        },
        {
            external_id: "4",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 6,
            match_number: 4,
            location: "Vandansk",
            date: "2025-10-24T12:08:39.000+00:00",
            determining_score: "Score 2",
            status: TournamentMatchStatus.UPCOMING,
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
            ]
        },
        {
            external_id: "5",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 4,
            match_number: 10,
            location: "Vandansk",
            date: "2025-11-24T12:08:39.000+00:00",
            determining_score: "Score 2",
            status: TournamentMatchStatus.UPCOMING,
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[1],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[2],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[3],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[4],
            ]
        },
        {
            external_id: "6",
            scores: {
                "Score 1": TournamentScoringSystem.RANK,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 41,
            match_number: 10,
            location: "Nuke Town",
            status: TournamentMatchStatus.ENDED,
            score: TournamentMatchEndScore.LOST,
            date: "2025-02-24T12:08:39.000+00:00",
            determining_score: "Score 1",
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[1],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[2],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[3],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[4],
            ]
        },
        {
            external_id: "7",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 12,
            match_number: 43,
            location: "Ajegunle Lagos State",
            status: TournamentMatchStatus.ENDED,
            score: TournamentMatchEndScore.WON,
            date: "2025-03-22T12:08:39.000+00:00",
            determining_score: "Score 1",
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[1],
            ]
        },
        {
            external_id: "8",
            scores: {
                "Score 1": TournamentScoringSystem.RANK,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 10,
            match_number: 9,
            location: "Ajegunle Lagos State",
            status: TournamentMatchStatus.CANCELLED,
            score: TournamentMatchEndScore.DRAW,
            date: "2025-04-22T12:08:39.000+00:00",
            cancellation_reason: "The reson is not set in stone, The reson is not set in stone, The reson is not set in stone, The reson is not set in stone, The reson is not set in stone, The reson is not set in stone.",
            determining_score: "Score 1",
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[4],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
            ]
        },
        {
            external_id: "9",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 19,
            match_number: 37,
            location: "Ajegunle Lagos State",
            status: TournamentMatchStatus.ENDED,
            score: TournamentMatchEndScore.FORFEITED,
            date: "2025-05-22T12:08:39.000+00:00",
            determining_score: "Score 2",
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
            ]
        },
        {
            external_id: "10",
            scores: {
                "Score 1": TournamentScoringSystem.POINTS,
                "Score 2": TournamentScoringSystem.DURATION,
            },
            group_number: 8,
            match_number: 102,
            location: "Ajegunle Lagos State",
            status: TournamentMatchStatus.ENDED,
            score: TournamentMatchEndScore.ABSENT,
            date: "2025-06-22T12:08:39.000+00:00",
            determining_score: "Score 1",
            participants: [
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[6],
                TournamentService.TEST_REGISTERED_TOURNAMENT_MATCHES_PARTICIPANTS[0],
            ]
        },
    ];

    static TEST_TOURNAMENT_SCORES: any[] = [
        {
            name: "#",
            external_id: "1",
            tournament_prize_pool_ids: [],
            score_system: TournamentScoringSystem.POINTS,
            type: TournamentEditionScoringType.TOURNAMENT_SCORE,
            applies_to: TournamentEditionScoringAppliesTo.PARTICIPANT,
            tournament_prize_pools: [

            ],
            leader: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[0]
        },
        {
            tournament_prize_pool_ids: [],
            external_id: "2",
            name: "Best Scorer",
            score_system: TournamentScoringSystem.RANK,
            type: TournamentEditionScoringType.CUSTOM_SCORE,
            applies_to: TournamentEditionScoringAppliesTo.INDIVIDUAL,
            tournament_prize_pools: [

            ],
            leader: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[1]
        },
        {
            external_id: "3",
            name: "Clean Sheet",
            tournament_prize_pool_ids: [],
            score_system: TournamentScoringSystem.DURATION,
            type: TournamentEditionScoringType.CUSTOM_SCORE,
            applies_to: TournamentEditionScoringAppliesTo.PARTICIPANT,
            tournament_prize_pools: [

            ],
            leader: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[1]
        }
    ];

    static TEST_TOURNAMENT_LEADERBOARD: any[] = [
        {
            place: 1,
            external_id: "1",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0] as any,
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[0],
            scores: {
                "Points": 100,
                "Goal Difference": 35,
            }
        },
        {
            place: 2,
            external_id: "2",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[1],
            scores: {
                "Points": 90,
                "Goal Difference": 15,
            }
        },
        {
            place: 3,
            external_id: "3",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[2],
            scores: {
                "Points": 80,
                "Goal Difference": 14,
            }
        },
        {
            place: 4,
            external_id: "4",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[1],
            scores: {
                "Points": 90,
                "Goal Difference": 15,
            }
        },
        {
            place: 5,
            external_id: "5",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[2],
            scores: {
                "Points": 80,
                "Goal Difference": 14,
            }
        },
        {
            place: 6,
            external_id: "6",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[1],
            scores: {
                "Points": 90,
                "Goal Difference": 15,
            }
        },
        {
            place: 7,
            external_id: "7",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[2],
            scores: {
                "Points": 80,
                "Goal Difference": 14,
            }
        },
        {
            place: 8,
            external_id: "8",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[2],
            scores: {
                "Points": 80,
                "Goal Difference": 14,
            }
        },
        {
            place: 9,
            external_id: "9",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[1],
            scores: {
                "Points": 90,
                "Goal Difference": 15,
            }
        },
        {
            place: 10,
            external_id: "10",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[2],
            scores: {
                "Points": 80,
                "Goal Difference": 14,
            }
        },
        {
            place: 11,
            external_id: "11",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[2],
            scores: {
                "Points": 80,
                "Goal Difference": 14,
            }
        },
        {
            place: 12,
            external_id: "12",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[1],
            scores: {
                "Points": 90,
                "Goal Difference": 15,
            }
        },
        {
            place: 13,
            external_id: "13",
            tournament_score: TournamentService.TEST_TOURNAMENT_SCORES[0],
            participant: TournamentService.TEST_REGISTERED_TOURNAMENTS_PARTICIPANTS[2],
            scores: {
                "Points": 80,
                "Goal Difference": 14,
            }
        },
    ];

}
